import React, { useEffect, useState, useMemo, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAppConfig } from "../ConfigContext";
import { usePostHog } from "posthog-js/react";
import { getSkinSrc } from "./SeasonalSkins";
import DiscountOfferModal from "../components/DiscountOfferModal";
import { FancySelect } from "./FancySelect"; // adjust path
import "../index.css";

const ABILITY_CHANGE_HINT_ENABLED = true;
const ABILITY_HINT_STORAGE_KEY = "ability_change_hint_seen_v1";

const API_BASE = process.env.REACT_APP_API_URL;

const normalizeItemType = (t) => {
  const s = String(t ?? "").trim().toLowerCase();

  if (
    s === "cardpieces" ||
    s === "cardpiece" ||
    s === "card_pieces" ||
    s === "card-pieces"
  ) {
    return "cardPieces";
  }

  return t;
};

function Fight({
  user,
  setUser,
  onNavigate,
  setSelectedCardId,
  openedVia = "menu",
  onOpenShop,
}) {
  const STORAGE_KEY = "fight_state_v2";

  const initialPersisted = useMemo(() => {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  }, [STORAGE_KEY]);

  const showBanner = 0;

  const posthog = usePostHog();
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);

  const [initialLoading, setInitialLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const enterBtnRef = useRef(null);

const [evolveOpen, setEvolveOpen] = useState(false);
const [evolveData, setEvolveData] = useState(null);
const [evolvingId, setEvolvingId] = useState(null);

const [evolveConfirmOpen, setEvolveConfirmOpen] = useState(false);
const [evolveConfirmCard, setEvolveConfirmCard] = useState(null);
const [pieceConvertOpen, setPieceConvertOpen] = useState(false);
const [pieceConvertCard, setPieceConvertCard] = useState(null);
const [pieceConvertBusy, setPieceConvertBusy] = useState(false);
const [pieceConvertSuccess, setPieceConvertSuccess] = useState(null);

const [invItems, setInvItems] = useState([]);
const [inventoryLoaded, setInventoryLoaded] = useState(false);
const [inventoryLoading, setInventoryLoading] = useState(false);
const [pieceClaimBusyId, setPieceClaimBusyId] = useState(null);
const [pieceClaimError, setPieceClaimError] = useState("");

  const [pendingCard, setPendingCard] = useState(null);
  const [entering, setEntering] = useState(false);

  const [battleSoonOpen, setBattleSoonOpen] = useState(false);
  const hydratedRef = useRef(false);

  const [abilityChangeOpen, setAbilityChangeOpen] = useState(false);
  const [abilityChangeBusy, setAbilityChangeBusy] = useState(false);
  const [abilityCard, setAbilityCard] = useState(null);
  const [abilityTarget, setAbilityTarget] = useState(null);

  const [showAbilityHint, setShowAbilityHint] = useState(false);
  const [hintCardId, setHintCardId] = useState(null);
  const [hintAbilityIndex, setHintAbilityIndex] = useState(0);

const [abilityChangeInfo, setAbilityChangeInfo] = useState({
  loading: false,
  enabled: false,
  used: 0,
  limit: 0,
  limitMode: "overall",
  nextPrice: null,
  basePrice: 0,
  payCurrency: null,
  resetAt: null,
  refillPriceRunes: 0,
  canRefill: false,
});

const [abilityRefillOpen, setAbilityRefillOpen] = useState(false);
const [abilityRefillBusy, setAbilityRefillBusy] = useState(false);

  const { shop,evolvements } = useAppConfig();


  //const evolvements = "" ;

useEffect(() => {
  console.log("[Fight] shop =", shop);
  console.log("[Fight] evolvements =", evolvements);
}, [shop, evolvements]);

  const [entryFee, setEntryFee] = useState(0);

  const [feeLoading, setFeeLoading] = useState(true);

  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [defenseDeckRequiredOpen, setDefenseDeckRequiredOpen] = useState(false);

  const [limitInfo, setLimitInfo] = useState({
    loading: true,
    used: 0,
    limit: 10,
    ok: false,
    resetAt: null,
  });
  const [cooldownLabel, setCooldownLabel] = useState(null);
  const [cardLimits, setCardLimits] = useState({});
  const [cardCooldowns, setCardCooldowns] = useState({});

  const [showFightRefillModal, setShowFightRefillModal] = useState(false);
  const [fightRefillLoading, setFightRefillLoading] = useState(null); // 'video' | 'stars' | null
  const [fightRefillError, setFightRefillError] = useState("");
  const fightRefillAdControllerRef = useRef(null);
  const [refillCard, setRefillCard] = useState(null);

  const [renameOpen, setRenameOpen] = useState(false);
  const [renamingCard, setRenamingCard] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [renameBusy, setRenameBusy] = useState(false);

  const rarityOptions = [
    { value: "all", label: "All Rarities" }, // keep your spelling if intentional
    { value: "common", label: "Common" },
    { value: "rare", label: "Rare" },
    { value: "epic", label: "Epic" },
    { value: "legendary", label: "Legendary" },
  ];

  const sortOptions = [
    { value: "availability", label: "Sort: Availability" },
    { value: "rarity", label: "Sort: Rarities" },
    { value: "power", label: "Sort: Power" },
    { value: "level", label: "Sort: Level" },
    { value: "wins", label: "Sort: Wins" },
    { value: "name", label: "Sort: Name" },
  ];

  const pillBtnStyle = {
    height: 42,
    width: 42, // square button for icon-only
    padding: 0,
    border: "none",
    borderRadius: "8px",
    backgroundColor: "transparent",
    backgroundImage: "url('/rect.png')",
    backgroundRepeat: "no-repeat",
    backgroundSize: "100% 42px",
    backgroundPosition: "left top",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  };

  const nameChangePrice = useMemo(() => {
    const raw =
      shop?.nameChangePrice ??
      shop?.renamePrice ??
      shop?.cardNamePrice ??
      shop?.cardRenamePrice ??
      shop?.NAME_CHANGE_PRICE ??
      0;
    const n = Number(raw);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  }, [shop]);

  const dismissAbilityHintForever = () => {
  localStorage.setItem(ABILITY_HINT_STORAGE_KEY, "true");
  setShowAbilityHint(false);
};

  const abilityChangeCurrency = abilityChangeInfo.payCurrency;
const abilityChangePrice = abilityChangeInfo.nextPrice;
const abilityChangeEnabled =
  !abilityChangeInfo.loading &&
  !!abilityChangeInfo.enabled &&
  !!abilityChangeCurrency &&
  Number(abilityChangePrice) > 0;

  
  const ABILITIES = useMemo(
    () => [
      "Berserk",
      "Pierce",
      "Frenzy",
      "Bleed",
      "Flameburst",
      "Execution",
      "Shield Wall",
      "Regen",
      "Thorns",
      "Aegis",
      "Purify",
      "Fortify",
    ],
    [],
  );

  const MAX_LEVEL = 10;

  const RARITY_PROFILES = useMemo(
    () => ({
      Common: { atk: [2, 5], def: [1, 4], hp: [14, 22], abilities: [0, 1] },
      Rare: { atk: [3, 6], def: [2, 5], hp: [18, 28], abilities: [1, 1] },
      Epic: { atk: [5, 8], def: [3, 7], hp: [24, 36], abilities: [1, 2] },
      Legendary: { atk: [7, 10], def: [5, 9], hp: [30, 44], abilities: [2, 3] },
    }),
    [],
  );

const [battleStreakInfo, setBattleStreakInfo] = useState({
  loading: true,
  active: false,          // event is available/enabled
  participating: false,   // user has joined/started
  totalSettled: 0,
  cycleSize: 0,
  progressInCycle: 0,
  currentLevel: 1,
  milestones: [],
  prevMilestone: 0,
  nextMilestone: null,
  remaining: 0,
});

  const battleStreakActive = !!battleStreakInfo?.active;

  const fetchBattleStreak = React.useCallback(async () => {
  const token = localStorage.getItem("token");
  if (!token) {
    setBattleStreakInfo((s) => ({ ...s, loading: false, active: false }));
    return;
  }

  setBattleStreakInfo((s) => ({ ...s, loading: true }));

  try {
    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/api/battle/streak`,
      { headers: { Authorization: `Bearer ${token}` } }
    );

    const data = await res.json().catch(() => ({}));

    if (!res.ok) {
      setBattleStreakInfo((s) => ({ ...s, loading: false }));
      return;
    }

   const milestonesArr = Array.isArray(data.milestones) ? data.milestones : [];
const cycle = Number(data.cycleSize ?? 0);

// ====== ✅ ACTIVE POR DATA (HOJE ENTRE START/END) ======
const toTs = (v) => {
  if (!v) return null;
  const t = new Date(v).getTime();
  return Number.isFinite(t) ? t : null;
};

// tenta vários nomes comuns que o backend pode mandar
const startTs =
  toTs(data?.startDate) ??
  toTs(data?.start_date) ??
  toTs(data?.startsAt) ??
  toTs(data?.starts_at);

const endTs =
  toTs(data?.endDate) ??
  toTs(data?.end_date) ??
  toTs(data?.endsAt) ??
  toTs(data?.ends_at);

const now = Date.now();

// se tiver start e end válidos: ativo se now estiver dentro (end inclusivo)
const activeByDate =
  startTs != null && endTs != null ? now >= startTs && now <= endTs : null;

// fallback: se o backend mandar flag explícita, usa ela; senão usa activeByDate;
// e se nem data vier, cai pra “configurado” (se você quiser)
const activeFromApi =
  data?.active ?? data?.isActive ?? data?.enabled ?? data?.eventActive;

const activeFlag =
  typeof activeFromApi === "boolean"
    ? activeFromApi
    : activeFromApi === 1 || String(activeFromApi).toLowerCase() === "true";

const configured = milestonesArr.length > 0 && cycle > 0;

const active =
  activeByDate != null ? activeByDate : activeFromApi != null ? activeFlag : configured;

const participatingFromApi =
  data?.participating ?? data?.isParticipating ?? data?.joined ?? data?.enrolled;

const participatingFallback =
  Number(data.totalSettled ?? 0) > 0 || Number(data.progressInCycle ?? 0) > 0;

const participating =
  typeof participatingFromApi === "boolean"
    ? participatingFromApi
    : participatingFallback;


    const progress = Number(data.progressInCycle ?? 0);

    const computedLevel = milestonesArr
      .map((m) => Number(m?.count ?? 0))
      .filter((n) => Number.isFinite(n) && n > 0)
      .sort((a, b) => a - b)
      .reduce((lvl, milestoneCount) => (progress >= milestoneCount ? lvl + 1 : lvl), 1);

    setBattleStreakInfo({
  loading: false,
  active,
  participating,
  totalSettled: Number(data.totalSettled ?? 0),
  cycleSize: cycle,
  progressInCycle: progress,
  currentLevel: computedLevel,
  milestones: milestonesArr,
  prevMilestone: Number(data.prevMilestone ?? 0),
  nextMilestone: data.nextMilestone == null ? null : Number(data.nextMilestone),
  remaining: Number(data.remaining ?? 0),
  startDate: data?.startDate ?? data?.start_date ?? data?.startsAt ?? data?.starts_at ?? null,
  endDate: data?.endDate ?? data?.end_date ?? data?.endsAt ?? data?.ends_at ?? null
});
  } catch {
    setBattleStreakInfo((s) => ({ ...s, loading: false }));
  }
}, []);

  

  const [nameQuery, setNameQuery] = useState(() =>
    String(initialPersisted.nameQuery ?? ""),
  );
  const [rarityFilter, setRarityFilter] = useState(() =>
    String(initialPersisted.rarityFilter ?? "all"),
  );
  const [sortKey, setSortKey] = useState(() =>
    String(initialPersisted.sortKey ?? "availability"),
  );
  const [sortDir, setSortDir] = useState(() =>
    String(initialPersisted.sortDir ?? "desc"),
  );

  const [debouncedQuery, setDebouncedQuery] = useState(() => {
    // debounced começa igual ao nameQuery restaurado, senão ele muda 250ms depois e pode disparar reset
    const q =
      initialPersisted.debouncedQuery ?? initialPersisted.nameQuery ?? "";
    return String(q).trim();
  });

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedQuery(nameQuery.trim());
    }, 250); // 250–400ms is fine

    return () => clearTimeout(t);
  }, [nameQuery]);

  const clamp01 = (x) => Math.max(0, Math.min(1, x));

  const MAX_CARD_NAME_LEN = 10;

 function persistSelectedCard(card) {
  if (!card?.id) return;

  setSelectedCardId?.(card.id);
  localStorage.setItem("selectedCardId", String(card.id));

  localStorage.setItem(
    "selectedCardSnapshot",
    JSON.stringify({
      id: card.id,
      name: card.name,
      rarity: card.rarity,
      attack: card.attack,
      defense: card.defense,
      health: card.health,
      level: card.level,
      wins: card.wins,
      pieces: card.pieces,
      character: card.character,
      side: card.side,
      seasonal_accessory: card.seasonal_accessory ?? null,
      season_name: card.season_name ?? null,
      abilities: card.abilities ?? [],
    })
  );
}

  const lower = (v) =>
    String(v ?? "")
      .trim()
      .toLowerCase();

  const getCharacterObj = (card) => {
    const raw = card?.character;
    if (!raw) return null;
    if (typeof raw === "object") return raw;
    if (typeof raw === "string") {
      try {
        return JSON.parse(raw);
      } catch {
        return null;
      }
    }
    return null;
  };

  const getCardSide = (card) => {
    if (card?.side) return lower(card.side);

    const ch = getCharacterObj(card);
    const bodySide = ch?.body?.side;
    if (bodySide) return lower(bodySide);

    if (ch && typeof ch === "object") {
      for (const part of Object.values(ch)) {
        if (part?.side) return lower(part.side);
      }
    }
    return null;
  };

  const truncateCardName = (name) => {
    const n = String(name || "");
    if (n.length <= MAX_CARD_NAME_LEN) return n;
    return n.slice(0, MAX_CARD_NAME_LEN - 1) + "…";
  };

  const normRarity = (r) => String(r || "Common").toLowerCase();

  const [page, setPage] = useState(() => {
    const p = Number(initialPersisted.page);
    return Number.isFinite(p) && p >= 1 ? p : 1;
  });

  useEffect(() => {
    const payload = {
      page,
      nameQuery,
      debouncedQuery,
      rarityFilter,
      sortKey,
      sortDir,
    };

    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch {}
  }, [
    page,
    nameQuery,
    debouncedQuery,
    rarityFilter,
    sortKey,
    sortDir,
    STORAGE_KEY,
  ]);

  const PAGE_SIZE = 13;
  const [totalCards, setTotalCards] = useState(null);

  const totalPages = useMemo(() => {
    if (totalCards == null) return null; // ainda não sei
    return Math.max(1, Math.ceil(totalCards / PAGE_SIZE));
  }, [totalCards, PAGE_SIZE]);

  const pagesReady = totalPages != null; // true só quando totalPages já é número

  const controlsKey = `${rarityFilter}|${debouncedQuery}|${sortKey}|${sortDir}`;
  const prevControlsRef = useRef(controlsKey);

  useEffect(() => {
    if (!hydratedRef.current) return;

    if (prevControlsRef.current !== controlsKey) {
      setPage(1);
      prevControlsRef.current = controlsKey;
    }
  }, [controlsKey]);

  const rarityRank = useMemo(
    () => ({ common: 1, rare: 2, epic: 3, legendary: 4 }),
    [],
  );

  const cardPower = (c) => {
    const atk = Number(c?.attack ?? 0);
    const def = Number(c?.defense ?? 0);
    const hp = Number(c?.health ?? 0);

    const raw = c?.abilities;
    const abilitiesCount = Array.isArray(raw)
      ? raw.length
      : (() => {
          try {
            return JSON.parse(raw || "[]")?.length || 0;
          } catch {
            return 0;
          }
        })();

    return atk + def + hp + abilitiesCount * 2;
  };

  const isOnCooldown = (card) => {
    const info = cardLimits?.[card.id];
    if (!info || info.loading) return false; // treat unknown as available (no jumping)
    return Number(info.used ?? 0) >= Number(info.limit ?? 0);
  };

  // Safety: if filtering reduces pages, clamp page down
  useEffect(() => {
    if (totalPages == null) return; // ✅ não clampa antes de saber
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const rewardPercent = useMemo(() => {
    const raw =
      shop?.rewardPercent ?? shop?.REWARD_PERCENT ?? shop?.fight?.rewardPercent;
    const n = Number(raw);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  }, [shop]);

  const { winBonus, projectedWin } = useMemo(() => {
    const fee = Number(entryFee || 0);
    const bonus = Math.floor(fee * (rewardPercent / 100));
    return {
      winBonus: Math.max(0, bonus),
      projectedWin: fee + Math.max(0, bonus),
    };
  }, [entryFee, rewardPercent]);

  const allBattleTaglines = [
    "Shuffle well. Fate hates sloppy decks. 🃏",
    "RNGesus is watching… and probably laughing.",
    "May your draws be blessed and your topdecks cruel.",
    "Time to turn cardboard into chaos!",
    "Stack your luck, play your bluff.",
    "One misplay away from greatness!",
    "Draw. Play. Pray. Repeat.",
    "Big brains, small cards, massive egos.",
    "Mana? Never enough. Confidence? Always too much.",
    "Legends are written in crits and misclicks.",
    "Lose gracefully, or win obnoxiously — your call.",
    "Every hand’s a new bad decision waiting to happen.",
    "If you believe in the heart of the cards, blink twice.",
    "Skill matters… right after luck.",
    "Prepare to deck-stroy your enemies!",
    "It’s not RNG — it’s “strategic unpredictability.”",
    "Your deck’s ready. Your dignity? Questionable.",
    "Card advantage is temporary. Style is forever.",
    "Draw 5, regret 4, play 1.",
    "Welcome to the shuffle zone — no refunds.",
  ];

  const battleTaglines = allBattleTaglines.filter((line) => line.length <= 42);

  const battleBullets = [
    "Draw wisely. Mulligan recklessly. 😎",
    "The real meta is emotional damage.",
    "Every deck tells a story… most end in tears.",
    "RNG is just skill wearing a disguise.",
    "Big brain plays, tiny cardboard soldiers.",
    "Tilt is free. Use it responsibly.",
    "Legends aren’t drawn — they’re topdecked.",
    "Outplay them. Outluck them. Out-meme them.",
    "Deck out? Blackout. Same thing.",
    "Remember: your opponent also thinks they’re the main character.",
  ];

  const [tagline, setTagline] = useState(battleTaglines[0]);
  const [bulletLines, setBulletLines] = useState(battleBullets);

  useEffect(() => {
    if (!battleSoonOpen) return;

    const randomTag =
      battleTaglines[Math.floor(Math.random() * battleTaglines.length)];
    setTagline(randomTag);

    const shuffled = [...battleBullets].sort(() => 0.5 - Math.random());
    setBulletLines(shuffled.slice(0, 1));
  }, [battleSoonOpen]);

  useEffect(() => {
    if (!battleSoonOpen || !pendingCard?.id) return;

    const info = cardLimits?.[pendingCard.id];

    // If batch limits haven't loaded yet, show "checking…"
    if (!info || info.loading) {
      setLimitInfo({
        loading: true,
        ok: false,
        used: 0,
        limit: 10,
        resetAt: null,
      });
      return;
    }

    setLimitInfo({
      loading: false,
      ok: true,
      used: Number(info.used ?? 0),
      limit: Number(info.limit ?? 10),
      resetAt: info.resetAt || null,
    });
  }, [battleSoonOpen, pendingCard?.id, cardLimits]);

 useEffect(() => {
  if (!battleSoonOpen) return;
  fetchBattleStreak();
}, [battleSoonOpen, fetchBattleStreak]);

  // Initialize Adsgram SDK for fight refill video
  useEffect(() => {
    const blockId =
      process.env.REACT_APP_ADSGRAM_FIGHT_REFILL_BLOCK_ID || "22709";

    if (
      typeof window !== "undefined" &&
      window.Adsgram &&
      blockId &&
      !fightRefillAdControllerRef.current
    ) {
      try {
        fightRefillAdControllerRef.current = window.Adsgram.init({
          blockId,
        });
      } catch (e) {
        console.warn("[Adsgram] Fight refill init failed:", e);
      }
    }
  }, []);

  useEffect(() => {
    if (
      limitInfo.loading ||
      !limitInfo.resetAt ||
      limitInfo.used < limitInfo.limit
    ) {
      setCooldownLabel(null);
      return;
    }

    const resetTs = new Date(limitInfo.resetAt).getTime();
    if (!Number.isFinite(resetTs)) {
      setCooldownLabel(null);
      return;
    }

    const update = () => {
      const diff = resetTs - Date.now();

      if (diff <= 0) {
        setLimitInfo((prev) => ({
          ...prev,
          used: 0,
          resetAt: null,
          loading: false,
        }));
        setCooldownLabel(null);
        return;
      }

      const totalSeconds = Math.floor(diff / 1000);
      const hours = String(Math.floor(totalSeconds / 3600)).padStart(2, "0");
      const minutes = String(Math.floor((totalSeconds % 3600) / 60)).padStart(
        2,
        "0",
      );
      const seconds = String(totalSeconds % 60).padStart(2, "0");

      setCooldownLabel(`${hours}:${minutes}:${seconds}`);
    };

    update();
    const id = window.setInterval(update, 1000);
    return () => window.clearInterval(id);
  }, [limitInfo.loading, limitInfo.resetAt, limitInfo.used, limitInfo.limit]);

  useEffect(() => {
    if (!pendingCard) return;

    const toNum = (v) => {
      const n = Number(v);
      return Number.isFinite(n) && n >= 0 ? n : 0;
    };

    const rarity = String(pendingCard.rarity || "Common").toLowerCase();

    const feeByRarity = {
      common: toNum(shop?.fightCost?.common ?? shop?.fight?.common),
      rare: toNum(shop?.fightCost?.rare ?? shop?.fight?.rare),
      epic: toNum(shop?.fightCost?.epic ?? shop?.fight?.epic),
      legendary: toNum(shop?.fightCost?.legendary ?? shop?.fight?.legendary),
    };

    setEntryFee(feeByRarity[rarity] ?? 0);
    setFeeLoading(false);
  }, [pendingCard, shop]);

  const EVOLVE_MULTIPLIER =
    Number(process.env.REACT_APP_EVOLVE_MULTIPLIER) || 1.25;
  function uiEvolveCost(card) {
    const base = nextEvolveCost(card.rarity);
    const timesEvolved = Math.max(0, (card.level || 1) - 1);
    return Math.ceil(base * Math.pow(EVOLVE_MULTIPLIER, timesEvolved));
  }

  const RUNE_COST_BY_RARITY = useMemo(
    () => ({
      common: Number(shop?.evolve?.common) || 5,
      rare: Number(shop?.evolve?.rare) || 10,
      epic: Number(shop?.evolve?.epic) || 15,
      legendary: Number(shop?.evolve?.legendary) || 20,
    }),
    [shop],
  );

  const fightRewards = useMemo(
    () => ({
      common: Number(shop?.fight?.common) || 0,
      rare: Number(shop?.fight?.rare) || 0,
      epic: Number(shop?.fight?.epic) || 0,
      legendary: Number(shop?.fight?.legendary) || 0,
    }),
    [shop],
  );

  const pickProfile = (rarity) => {
    const key = normRarity(rarity);
    if (key === "common") return RARITY_PROFILES.Common;
    if (key === "rare") return RARITY_PROFILES.Rare;
    if (key === "epic") return RARITY_PROFILES.Epic;
    if (key === "legendary") return RARITY_PROFILES.Legendary;
    return RARITY_PROFILES.Common;
  };

  const fightRewardFor = (rarity) => fightRewards[normRarity(rarity)] ?? 0;
  const nextEvolveCost = (rarity) =>
    RUNE_COST_BY_RARITY[normRarity(rarity)] ?? 5;

    useEffect(() => {
  if (!ABILITY_CHANGE_HINT_ENABLED) {
    setShowAbilityHint(false);
    return;
  }

  const alreadySeen =
    localStorage.getItem(ABILITY_HINT_STORAGE_KEY) === "true";

  if (alreadySeen) {
    setShowAbilityHint(false);
    return;
  }

  const firstEligibleCard = cards.find((card) => {
    const abilities = sanitizeAbilityList(parseAbilities(card?.abilities));
    return abilities.length > 0;
  });

  if (!firstEligibleCard) {
    setShowAbilityHint(false);
    return;
  }

  setHintCardId(firstEligibleCard.id);
  setHintAbilityIndex(0);
  setShowAbilityHint(true);

  const timeout = setTimeout(() => {
    setShowAbilityHint(false);
  }, 5000);

  return () => clearTimeout(timeout);
}, [cards]);

function getEvolveOptions(card) {
  const rarity = String(card?.rarity || "").toLowerCase();
  const level = Number(card?.level || 1);

  const list = evolvements?.prices?.[rarity];

  if (Array.isArray(list)) {
    const row = list.find((p) => Number(p?.level) === level);

    const runes = Number(row?.runes || 0);
    const cardPieces = Number(row?.cardPieces || 0);
    const sparks = Number(row?.sparks || 0);

    if (runes > 0 || cardPieces > 0 || sparks > 0) {
      return {
        isLegacy: false,
        options: {
          runes,
          cardPieces,
          sparks,
        },
      };
    }
  }

  return {
    isLegacy: true,
    options: {
      sparks: uiEvolveCost(card),
    },
  };
}

const isCardPiecesItem = (inv) => {
  return normalizeItemType(inv?.item?.type) === "cardPieces";
};

const getCardPieceItemAmount = (inv) => {
  const raw =
    inv?.amount ??
    inv?.item?.card_pieces_amount ??
    inv?.item?.cardPiecesAmount ??
    inv?.item?.pieces ??
    inv?.quantity ??
    0;

  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

const claimableCardPieceItems = useMemo(() => {
  return invItems
    .filter((inv) => isCardPiecesItem(inv))
    .filter((inv) => !inv.claimed)
    .filter((inv) => getCardPieceItemAmount(inv) > 0);
}, [invItems]);

const ensureInventoryLoaded = async () => {
  if (inventoryLoaded || inventoryLoading) return;

  try {
    setInventoryLoading(true);
    setPieceClaimError("");

    const token = localStorage.getItem("token");
    if (!token) {
      throw new Error("Not logged in.");
    }

    const res = await fetch(`${API_BASE}/api/inventory`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    const data = await res.json().catch(() => ({}));

    if (!res.ok) {
      throw new Error(data?.error || "Failed to load inventory.");
    }

    setInvItems(Array.isArray(data.items) ? data.items : []);
    setInventoryLoaded(true);
  } catch (err) {
    setPieceClaimError(err?.message || "Failed to load card pieces.");
  } finally {
    setInventoryLoading(false);
  }
};

const claimCardPiecesIntoCard = async (inv, card) => {
  if (!inv?.user_item_id || !card?.id) return;
  if (pieceClaimBusyId) return;

  try {
    setPieceClaimBusyId(inv.user_item_id);
    setPieceClaimError("");

    const token = localStorage.getItem("token");
    if (!token) {
      throw new Error("Not logged in.");
    }

    const res = await fetch(
      `${API_BASE}/api/inventory/${encodeURIComponent(inv.user_item_id)}/claim`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ card_id: card.id }),
      }
    );

    const data = await res.json().catch(() => ({}));

    if (!res.ok) {
      throw new Error(data?.error || "Failed to claim card pieces.");
    }

    setInvItems((prev) =>
      prev.map((row) =>
        row.user_item_id === inv.user_item_id
          ? {
              ...row,
              claimed: true,
              claimed_at:
                data.claimed_at ||
                row.claimed_at ||
                new Date().toISOString(),
              applied_card_id:
                data.applied_card_id ?? data.card?.id ?? card.id,
            }
          : row
      )
    );

    if (data.card) {
      setCards((prev) =>
        prev.map((c) =>
          Number(c.id) === Number(data.card.id)
            ? { ...c, ...data.card }
            : c
        )
      );

      setEvolveConfirmCard((prev) =>
        prev && Number(prev.id) === Number(data.card.id)
          ? { ...prev, ...data.card }
          : prev
      );
    }
  } catch (err) {
    setPieceClaimError(err?.message || "Failed to claim card pieces.");
  } finally {
    setPieceClaimBusyId(null);
  }
};

  const parseAbilities = (raw) => {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    try {
      return JSON.parse(raw);
    } catch {
      return [];
    }
  };

  const sanitizeAbilityList = (list) =>
    (list || [])
      .map((a) => (typeof a === "string" ? a : a?.name || ""))
      .map((s) => String(s).trim())
      .filter((s) => s && ABILITIES.includes(s));

  const underAbilityCap = (abilities, rarity) => {
    const prof = pickProfile(rarity);
    const [, maxAbilities] = prof.abilities;
    return (abilities?.length || 0) < maxAbilities;
  };

  const canCardImprove = (card) => {
    const prof = pickProfile(card.rarity);
    const canStat =
      (card.attack ?? 0) < prof.atk[1] ||
      (card.defense ?? 0) < prof.def[1] ||
      (card.health ?? 0) < prof.hp[1];

    const abilitiesNow = sanitizeAbilityList(parseAbilities(card.abilities));
    const canAb = underAbilityCap(abilitiesNow, card.rarity);

    return canStat || canAb;
  };

  const loadAbilityChangeInfo = async (cardId) => {
  if (!cardId) {
    throw new Error("Missing card id");
  }

  const res = await fetch(
    `${process.env.REACT_APP_API_URL}/api/cards/${encodeURIComponent(cardId)}/ability-change-info`,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    }
  );

  const data = await res.json().catch(() => ({}));

  console.log("ABILITY_CHANGE_INFO_RESPONSE", data);

  if (!res.ok) {
    throw new Error(data?.error || "Failed to load ability change info");
  }

 const nextState = {
  loading: false,
  enabled: !!data.enabled,
  used: Number.isFinite(Number(data.used)) ? Number(data.used) : 0,
  limit: Number.isFinite(Number(data.limit)) ? Number(data.limit) : 0,
  limitMode: data.limitMode === "overall" ? "overall" : "daily",
  nextPrice:
    data.nextPrice == null || !Number.isFinite(Number(data.nextPrice))
      ? null
      : Number(data.nextPrice),
  basePrice:
    data.basePrice == null || !Number.isFinite(Number(data.basePrice))
      ? 0
      : Number(data.basePrice),
  payCurrency: data.payCurrency || null,
  resetAt: data.resetAt || null,
  refillPriceRunes: Number(data.refillPriceRunes ?? 0),
  canRefill: !!data.canRefill,
};

  setAbilityChangeInfo(nextState);
  return nextState;
};

const openAbilityChange = async (card, ability) => {
  if (!card) return;

  try {
    setAbilityChangeInfo((prev) => ({
      ...prev,
      loading: true,
    }));

    const freshInfo = await loadAbilityChangeInfo(card.id);

    if (
  Number.isFinite(Number(freshInfo.limit)) &&
  Number.isFinite(Number(freshInfo.used)) &&
  freshInfo.limit > 0 &&
  freshInfo.used >= freshInfo.limit
) {
  if (Number(freshInfo.refillPriceRunes ?? 0) > 0) {
    setAbilityCard(card);
    setAbilityTarget(ability);
    setAbilityRefillOpen(true);
    return;
  }

  setErrorMessage(
    freshInfo.limitMode === "overall"
      ? "Limit reached for ability changes."
      : "Daily limit reached for ability changes."
  );
  setShowErrorModal(true);
  return;
}

    if (
      !freshInfo.enabled ||
      !freshInfo.payCurrency ||
      !Number.isFinite(Number(freshInfo.nextPrice)) ||
      Number(freshInfo.nextPrice) <= 0
    ) {
      setAbilityChangeOpen(false);
      setAbilityCard(null);
      setAbilityTarget(null);
      return;
    }

    setAbilityCard(card);
    setAbilityTarget(ability);
    setAbilityChangeOpen(true);
  } catch (err) {
    setErrorMessage(err.message || "Failed to load ability change info");
    setShowErrorModal(true);
    setAbilityChangeInfo({
      loading: false,
      enabled: false,
      used: 0,
      limit: 0,
      limitMode: "overall",
      nextPrice: null,
      basePrice: 0,
      payCurrency: null,
      resetAt: null,
      refillPriceRunes: 0,
      canRefill: false,
    });
  }
};

const resetAbilityLimit = async () => {
  try {
    if (!abilityCard?.id) return;

    setAbilityRefillBusy(true);

    const token = localStorage.getItem("token");
    if (!token) throw new Error("Not logged in.");

    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/api/cards/${encodeURIComponent(abilityCard.id)}/reset-ability-change-limit`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    const data = await res.json().catch(() => ({}));

    if (!res.ok) {
      throw new Error(data?.error || "Failed to reset ability limit");
    }

    if (typeof setUser === "function") {
      setUser((u) => ({
        ...(u || {}),
        runes: Number(data?.runes ?? u?.runes ?? 0),
      }));
    }

    const freshInfo = await loadAbilityChangeInfo(abilityCard.id);

    setAbilityRefillOpen(false);

    if (
      freshInfo?.enabled &&
      freshInfo?.payCurrency &&
      Number(freshInfo?.nextPrice) > 0 &&
      abilityTarget
    ) {
      setAbilityChangeOpen(true);
    }
  } catch (err) {
    setErrorMessage(err?.message || "Failed to reset ability limit");
    setShowErrorModal(true);
  } finally {
    setAbilityRefillBusy(false);
  }
};

const openDefenseDeckRequiredModal = () => {
  setBattleSoonOpen(false);
  setPendingCard(null);
  setEntering(false);
  setDefenseDeckRequiredOpen(true);
};

const goToDefenseDeck = () => {
  setDefenseDeckRequiredOpen(false);
  onNavigate?.("defenseDeck");
};


const submitAbilityChange = async () => {
  if (!abilityCard || !abilityTarget) return;

  try {
    setAbilityChangeBusy(true);

    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/api/cards/${encodeURIComponent(abilityCard.id)}/change-ability`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({ ability: abilityTarget }),
      }
    );

    const data = await res.json().catch(() => ({}));

    if (!res.ok) {
  if (
  data?.error === "ABILITY_CHANGE_LIMIT_REACHED" ||
  data?.error === "ABILITY_CHANGE_DAILY_LIMIT_REACHED"
) {
  const resolvedLimitMode =
    data?.limitMode === "overall" || abilityChangeInfo.limitMode === "overall"
      ? "overall"
      : "daily";

  const refillPriceRunes = Number(
    data.refillPriceRunes ?? abilityChangeInfo.refillPriceRunes ?? 0
  );

  setAbilityChangeInfo({
    loading: false,
    enabled: false,
    used: Number(data.used ?? 0),
    limit: Number(data.limit ?? 5),
    limitMode: resolvedLimitMode,
    nextPrice: null,
    basePrice: Number(data.basePrice ?? 0),
    payCurrency: data.payCurrency || null,
    resetAt: data.resetAt || null,
    refillPriceRunes,
    canRefill: refillPriceRunes > 0,
  });

  setAbilityChangeOpen(false);

  if (refillPriceRunes > 0) {
    setAbilityRefillOpen(true);
    return;
  }

  setAbilityCard(null);
  setAbilityTarget(null);

  setErrorMessage(
    resolvedLimitMode === "overall"
      ? "Overall limit reached for ability changes."
      : "Daily limit reached for ability changes."
  );
  setShowErrorModal(true);
  return;
}

  throw new Error(data?.error || "Failed to change ability");
}

    if (data.card) {
      setCards((prev) =>
        prev.map((c) => (c.id === data.card.id ? { ...c, ...data.card } : c))
      );
    }

    if (data.user?.runes !== undefined || data.user?.sparks !== undefined) {
      setUser((prev) => ({ ...(prev || {}), ...(data.user || {}) }));
    }

    setAbilityChangeInfo({
      loading: false,
      enabled: true,
      used: Number(data.used ?? 0),
      limit: Number(data.limit ?? 5),
      limitMode: data.limitMode === "overall" ? "overall" : "daily",
      nextPrice: data.nextPrice == null ? null : Number(data.nextPrice),
      basePrice: Number(data.basePrice ?? 0),
      payCurrency: data.payCurrency || null,
      resetAt: data.resetAt || null,
      refillPriceRunes: Number(data.refillPriceRunes ?? 0),
      canRefill: !!data.canRefill,
    });

    setAbilityChangeOpen(false);
    setAbilityCard(null);
    setAbilityTarget(null);
  } catch (err) {
    setAbilityChangeOpen(false);
    setAbilityCard(null);
    setAbilityTarget(null);

    setErrorMessage(err.message || "Failed to change ability");
    setShowErrorModal(true);
  } finally {
    setAbilityChangeBusy(false);
  }
};

  const openRename = (card) => {
    setRenamingCard(card);
    setRenameValue(card?.name || "");
    setRenameOpen(true);
  };

  const submitRename = async (e) => {
    e?.preventDefault?.();
    if (!renamingCard) return;

    const trimmed = String(renameValue || "").trim();
    if (!trimmed) {
      setErrorMessage("Please enter a name.");
      setShowErrorModal(true);
      return;
    }
    if (trimmed.length > 32) {
      setErrorMessage("Name is too long (max 32 chars).");
      setShowErrorModal(true);
      return;
    }

    const have = Number(user?.sparks ?? 0);
    const need = Number(nameChangePrice ?? 0);
    if (need > 0 && have < need) {
      setErrorMessage(
        `You need ${need} Sparks to rename, but you only have ${have}.`,
      );
      setShowErrorModal(true);
      return;
    }

    try {
      setRenameBusy(true);
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/cards/${encodeURIComponent(
          renamingCard.id,
        )}/rename`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({ name: trimmed }),
        },
      );
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data?.error || "Failed to rename card");
      }

      if (data.card) {
        setCards((prev) =>
          prev.map((c) => (c.id === data.card.id ? { ...c, ...data.card } : c)),
        );
      }

      if (data.user?.sparks !== undefined && typeof setUser === "function") {
        setUser((prev) => ({ ...(prev || {}), sparks: data.user.sparks }));
      }

      setRenameOpen(false);
      setRenamingCard(null);
      setRenameValue("");
    } catch (err) {
      setErrorMessage(err.message || "Failed to rename card");
      setShowErrorModal(true);
    } finally {
      setRenameBusy(false);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const fetchCards = async () => {
      try {
        // ✅ não desmonta a tela quando digita
        if (initialLoading) setLoading(true);
        setIsFetching(true);

        const token = localStorage.getItem("token");
        const limit = PAGE_SIZE;
        const offset = (page - 1) * PAGE_SIZE;

        const params = new URLSearchParams();
        params.set("limit", String(limit));
        params.set("offset", String(offset));

        // filtros
        if (rarityFilter !== "all") params.set("rarity", rarityFilter);
        if (debouncedQuery) params.set("q", debouncedQuery);

        // sort
        params.set("sortKey", sortKey);
        params.set("sortDir", sortDir);

        const url = `${
          process.env.REACT_APP_API_URL
        }/api/shop/mine?${params.toString()}`;

        const res = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const data = await res.json().catch(() => ({}));
        if (cancelled) return;

        if (!res.ok) throw new Error(data?.error || "Failed to load cards");

        setCards(Array.isArray(data.cards) ? data.cards : []);
        setTotalCards(Number(data.total || 0));
      } catch (err) {
        if (!cancelled) {
          console.error("Failed to load user cards:", err);
          setCards([]);
          setTotalCards(0);
        }
      } finally {
        if (!cancelled) {
          setIsFetching(false);
          if (initialLoading) {
            setLoading(false);
            setInitialLoading(false);
            hydratedRef.current = true;
          }
        }
      }
    };

    fetchCards();
    return () => {
      cancelled = true;
    };
    // IMPORTANTE: depende do debouncedQuery, NÃO do nameQuery
  }, [page, rarityFilter, debouncedQuery, sortKey, sortDir]);

  const cardsIdKey = useMemo(() => cards.map((c) => c.id).join("|"), [cards]);

  const limitsInFlightRef = useRef(new Set());
  const limitsDoneRef = useRef(new Set());

  useEffect(() => {
    if (!cards.length) return;

    let cancelled = false;

    (async () => {
      const token = localStorage.getItem("token");
      if (!token) return;

      // request limits for visible cards only
      const need = cards
        .map((c) => c.id)
        .filter(
          (id) =>
            !limitsDoneRef.current.has(id) &&
            !limitsInFlightRef.current.has(id),
        );

      if (need.length === 0) return;

      // mark in-flight (NOT done)
      need.forEach((id) => limitsInFlightRef.current.add(id));

      // optional: set loading flags
      setCardLimits((prev) => {
        const next = { ...(prev || {}) };
        for (const id of need)
          next[id] = { ...(next[id] || {}), loading: true };
        return next;
      });

      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/api/battle/limits`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ cardIds: need }),
          },
        );

        const data = await res.json().catch(() => ({}));
        if (cancelled) return;

        if (!res.ok || !data?.limitsByCardId) {
          throw new Error(data?.error || "limits failed");
        }

        // normalize values so comparisons always work
        const normalized = {};
        for (const [id, info] of Object.entries(data.limitsByCardId)) {
          normalized[id] = {
            loading: false,
            used: Number(info?.used ?? info?.usedToday ?? 0),
            limit: Number(info?.limit ?? 10),
            resetAt: info?.resetAt ?? info?.reset_at ?? null,
          };
        }

        setCardLimits((prev) => ({ ...(prev || {}), ...normalized }));

        // mark done only after success
        need.forEach((id) => limitsDoneRef.current.add(id));
      } catch (e) {
        // allow retry later
        setCardLimits((prev) => {
          const next = { ...(prev || {}) };
          for (const id of need)
            next[id] = { loading: false, used: 0, limit: 10, resetAt: null };
          return next;
        });
      } finally {
        need.forEach((id) => limitsInFlightRef.current.delete(id));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [cardsIdKey]); // depend on visible page cards

  useEffect(() => {
    if (!Object.keys(cardLimits).length) return;

    const update = () => {
      const next = {};

      Object.entries(cardLimits).forEach(([cardId, info]) => {
        if (!info || info.loading || !info.resetAt || info.used < info.limit) {
          return;
        }

        const resetTs = new Date(info.resetAt).getTime();
        if (!Number.isFinite(resetTs)) return;

        const diff = resetTs - Date.now();
        if (diff <= 0) return;

        const totalSeconds = Math.floor(diff / 1000);
        const hours = String(Math.floor(totalSeconds / 3600)).padStart(2, "0");
        const minutes = String(Math.floor((totalSeconds % 3600) / 60)).padStart(
          2,
          "0",
        );
        const seconds = String(totalSeconds % 60).padStart(2, "0");

        next[cardId] = `${hours}:${minutes}:${seconds}`;
      });

      setCardCooldowns(next);
    };

    update();
    const id = window.setInterval(update, 1000);
    return () => window.clearInterval(id);
  }, [cardLimits]);

  const FancyLoader = ({ user }) => (
    <div
      className="fight-container"
      style={{
        backgroundImage: `url(${
          user?.side === "good" ? "/tap_bg_1.png" : "/fight_bg.png"
        })`,
        backgroundSize: "110%",
        backgroundRepeat: "repeat",
        backgroundPosition: "left -1px top",
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
        gap: 16,
      }}
    >

      <motion.div
        animate={{ rotate: 360 }}
        transition={{ repeat: Infinity, duration: 1.2, ease: "linear" }}
        style={{
          marginBottom: "130px",
          width: 90,
          height: 90,
          borderRadius: "50%",
          border: "6px solid transparent",
          borderTopColor: "#ffcc62",
          borderRightColor: "#9adcfb",
          boxShadow: "0 0 18px rgba(255,204,98,0.35)",
        }}
      />
    </div>
  );

  const OUT_OF_SPARKS_TEXT = "Not enough Sparks";

  function SparkHint({ show, text = OUT_OF_SPARKS_TEXT }) {
    return (
      <AnimatePresence initial={false}>
        {show && (
          <motion.div
            initial={{ opacity: 0, y: 6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98, filter: "blur(2px)" }}
            transition={{ duration: 0.22, ease: "easeOut" }}
            style={{
              position: "absolute",
              left: "50%",
              bottom: "100%",
              transform: "translate(-50%, -8px)",
              padding: "6px 10px",
              borderRadius: 10,
              fontSize: 13,
              color: "#fff",
              background: "rgba(0,0,0,0.75)",
              border: "1px solid rgba(255,255,255,0.25)",
              whiteSpace: "nowrap",
              pointerEvents: "none",
              zIndex: 20,
            }}
            aria-live="polite"
          >
            {text}
          </motion.div>
        )}
      </AnimatePresence>
    );
  }

  const SparkGuardButton = React.forwardRef(function SparkGuardButton(
    {
      user,
      onConfirm,
      children,
      hintText = OUT_OF_SPARKS_TEXT,
      style,
      disabled,
      ...props
    },
    ref,
  ) {
    const [showHint, setShowHint] = React.useState(false);
    const timerRef = React.useRef(null);
    const buttonRef = React.useRef(null);

    const getUserSparks = (u) =>
      Number(u?.sparks ?? u?.credits ?? u?.balance ?? 0);
    const hasSparks = Number.isFinite(getUserSparks(user))
      ? getUserSparks(user) > 0
      : true;

    const revealHint = (ms = 1400) => {
      setShowHint(true);
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setShowHint(false), ms);
    };

    React.useImperativeHandle(ref, () => ({
      focus: () => buttonRef.current?.focus(),
      showHint: (ms) => revealHint(ms),
    }));

    const handleClick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (hasSparks) {
        onConfirm?.();
        return;
      }
      revealHint();
    };

    React.useEffect(() => () => clearTimeout(timerRef.current), []);

    return (
      <div style={{ position: "relative", display: "inline-block" }}>
        <SparkHint show={showHint} text={hintText} />
        <button
          ref={buttonRef}
          onClick={handleClick}
          disabled={!!disabled}
          aria-disabled={!!disabled || !hasSparks}
          style={{
            minWidth: 120,
            opacity: disabled ? 0.7 : hasSparks ? 1 : 0.7,
            cursor: disabled
              ? "not-allowed"
              : hasSparks
              ? "pointer"
              : "not-allowed",
            ...style,
          }}
          {...props}
        >
          {children}
        </button>
      </div>
    );
  });

const openEvolveConfirm = async (card) => {
  if (!card) return;
  if (evolvingId) return;

  if ((card?.level ?? 1) >= MAX_LEVEL) {
    setErrorMessage("This card is already at level 10.");
    setShowErrorModal(true);
    return;
  }

  setPieceClaimError("");
  setEvolveConfirmCard(card);
  setEvolveConfirmOpen(true);

  const p = getEvolveOptions(card);
  const cardPieces = Number(p.options?.cardPieces || 0);

  if (cardPieces > 0) {
    await ensureInventoryLoaded();
  }
};

const getPieceConversionRate = (card) => {
  const raw =
    card?.pieceConversionRate ??
    card?.cardPiecesToSparksRate ??
    shop?.cardPiecesToSparksRate ??
    shop?.CARD_PIECES_TO_SPARKS_RATE ??
    0;

  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

const openPieceConvertConfirm = (card) => {
  if (!card) return;

  const level = Number(card.level || 1);
  const pieces = Number(card.pieces || 0);

  if (level !== MAX_LEVEL) {
    setErrorMessage("Only level 10 cards can convert pieces into sparks.");
    setShowErrorModal(true);
    return;
  }

  if (pieces <= 0) {
    setErrorMessage("This card has no pieces to convert.");
    setShowErrorModal(true);
    return;
  }

  setPieceConvertCard(card);
  setPieceConvertOpen(true);
};

const closePieceConvertConfirm = () => {
  if (pieceConvertBusy) return;
  setPieceConvertOpen(false);
  setPieceConvertCard(null);
};

const confirmPieceConvert = async () => {
  if (!pieceConvertCard?.id || pieceConvertBusy) return;

  try {
    setPieceConvertBusy(true);

    const res = await fetch(
      `${API_BASE}/api/cards/${encodeURIComponent(
        pieceConvertCard.id
      )}/convert-pieces-to-sparks`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );

    const data = await res.json().catch(() => ({}));

    if (!res.ok) {
      throw new Error(
        data?.message ||
        data?.error ||
        "Failed to convert card pieces."
      );
    }

    if (data.card) {
      setCards((prev) =>
        prev.map((card) =>
          Number(card.id) === Number(pieceConvertCard.id)
            ? { ...card, ...data.card }
            : card
        )
      );
    }

    if (data.user && typeof setUser === "function") {
      setUser((prev) => ({
        ...(prev || {}),
        ...data.user,
      }));
    }

    setPieceConvertOpen(false);
    setPieceConvertCard(null);

    setPieceConvertSuccess({
      piecesConverted: Number(data.piecesConverted || 0),
      sparksAwarded: Number(data.sparksAwarded || 0),
    });
  } catch (err) {
    setErrorMessage(err?.message || "Failed to convert card pieces.");
    setShowErrorModal(true);
  } finally {
    setPieceConvertBusy(false);
  }
};

const confirmEvolve = async (payWith) => {
  if (!evolveConfirmCard) return;
  if (evolvingId) return;

  const card = evolveConfirmCard;

  setPieceClaimError("");
  setEvolveConfirmOpen(false);
  setEvolveConfirmCard(null);

  await handleEvolve(card, payWith);
};

const handleEvolve = async (card, payWith) => {
  if (evolvingId) return;
  if ((card?.level ?? 1) >= MAX_LEVEL) {
    setErrorMessage("This card is already at level 10.");
    setShowErrorModal(true);
    return;
  }

  setEvolvingId(card.id);

  const oldStatsFallback = {
    attack: card.attack,
    defense: card.defense,
    health: card.health,
  };
  const abilitiesBefore = sanitizeAbilityList(parseAbilities(card.abilities));

  try {
    
    const useNewRoute =
  payWith === "runes" ||
  payWith === "pieces" ||
  payWith === "sparks";

const url = useNewRoute
  ? `${process.env.REACT_APP_API_URL}/api/cards/${card.id}/evolve-new`
  : `${process.env.REACT_APP_API_URL}/api/cards/${card.id}/evolve`;

const res = await fetch(url, {
  method: "POST",
  headers: {
    "Content-Type": "application/json",
    Authorization: `Bearer ${localStorage.getItem("token")}`,
  },
  body: JSON.stringify(payWith ? { payWith } : {}),
});

    const data = await res.json();

    if (!res.ok) {
      if (data?.error === "EVOLVE_NO_PROGRESS") {
        setErrorMessage("Already at max allowed for this card type.");
        setShowErrorModal(true);
      } else {
        throw new Error(data?.error || "Failed to evolve card");
      }
      return;
    }

    const updatedCard = data.card;

    setCards((prev) =>
      prev.map((c) => (c.id === card.id ? { ...c, ...updatedCard } : c)),
    );

   if (data.user && typeof setUser === "function") {
  setUser((prev) => ({ ...(prev || {}), ...(data.user || {}) }));
}

    const oldStats = data.oldStats || oldStatsFallback;
    const newStats = data.newStats || {
      attack: updatedCard.attack,
      defense: updatedCard.defense,
      health: updatedCard.health,
    };

    const abilitiesAfter = sanitizeAbilityList(parseAbilities(updatedCard.abilities));
    const gained = abilitiesAfter.find((a) => !abilitiesBefore.includes(a)) || null;

    setEvolveData({
      name: updatedCard.name || card.name,
      rarity: updatedCard.rarity || card.rarity,
      oldStats,
      newStats,
      abilityGained: gained,
    });
    setEvolveOpen(true);
  } catch (err) {
    setErrorMessage(err.message || "Purchase failed");
    setShowErrorModal(true);
  } finally {
    setEvolvingId(null);
  }
};

  const handleFightRefillVideo = async () => {
    setFightRefillError("");

    if (!fightRefillAdControllerRef.current) {
      setFightRefillError("Ad service not available");
      return;
    }

    setFightRefillLoading("video");
    try {
      await fightRefillAdControllerRef.current.show();

      // Frontend-confirmed: call backend to grant +1 fight for this card
      const token = localStorage.getItem("token");
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/battle/fights-refill-video`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(refillCard?.id ? { cardId: refillCard.id } : {}),
        },
      );

      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        setFightRefillError(data?.error || "Could not grant extra fight");
        return;
      }

      // Refresh limits for this card so UI unlocks without reload
      if (refillCard?.id) {
        try {
          const limRes = await fetch(
            `${process.env.REACT_APP_API_URL}/api/battle/limits`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({ cardIds: [refillCard.id] }),
            },
          );
          const limData = await limRes.json().catch(() => ({}));
          if (limRes.ok && limData?.limitsByCardId) {
            const info = limData.limitsByCardId[refillCard.id];
            if (info) {
              setCardLimits((prev) => ({
                ...(prev || {}),
                [refillCard.id]: {
                  loading: false,
                  used: Number(info.used ?? 0),
                  limit: Number(info.limit ?? 10),
                  resetAt: info.resetAt || null,
                },
              }));
            }
          }
        } catch {
          // non-fatal; limit will self-correct on next fetch
        }
      }

      setShowFightRefillModal(false);
      setRefillCard(null);
    } catch (error) {
      if (error?.error) {
        setFightRefillError("Ad failed to load – try again");
      } else {
        setFightRefillError("Ad was skipped or closed");
      }
    } finally {
      setFightRefillLoading(null);
    }
  };

  const handleFightRefillStars = async () => {
    setFightRefillError("");

    const isTMA =
      typeof window !== "undefined" && !!window.Telegram?.WebApp?.openInvoice;
    if (!isTMA) {
      setFightRefillError(
        "Buying fights with Stars is only available in the Telegram app.",
      );
      return;
    }

    setFightRefillLoading("stars");
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/battle/fights-refill-stars/create-order`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({ cardId: refillCard?.id }),
        },
      );

      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        setFightRefillError(data?.error || "Could not start payment");
        setFightRefillLoading(null);
        return;
      }

      const { orderId, invoiceLink } = data;
      window.Telegram.WebApp.openInvoice(invoiceLink, async (status) => {
        if (status !== "paid") {
          setFightRefillError(
            status === "cancelled" ? "Payment cancelled" : "Payment failed",
          );
          setFightRefillLoading(null);
          return;
        }

        let tries = 0;
        const maxTries = 20;
        const wait = (ms) => new Promise((r) => setTimeout(r, ms));

        while (tries < maxTries) {
          const chk = await fetch(
            `${process.env.REACT_APP_API_URL}/api/telegram/stars/order/${orderId}`,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            },
          );

          if (chk.ok) {
            const j = await chk.json().catch(() => ({}));
            if (j.status === "paid") break;
          }

          tries++;
          await wait(1500);
        }

        setFightRefillLoading(null);
        setShowFightRefillModal(false);
        setRefillCard(null);
      });
    } catch (e) {
      setFightRefillError(e?.message || "Failed to buy fights");
      setFightRefillLoading(null);
    }
  };

  const isDefaultFilters =
    nameQuery === "" &&
    rarityFilter === "all" &&
    sortKey === "availability" &&
    sortDir === "desc";

  const limitReached =
    limitInfo.ok && !limitInfo.loading && limitInfo.used >= limitInfo.limit;
  const nextBattle =
    limitInfo.ok && !limitInfo.loading
      ? Math.min(limitInfo.used + 1, limitInfo.limit)
      : null;
  const enterDisabled = entering || feeLoading || !limitInfo.ok || limitReached;

  const FIGHT_REFILL_VIDEO_FIGHTS = 1;
  const FIGHT_REFILL_STARS_COST = 200;
  const FIGHT_REFILL_STARS_FIGHTS = 10;

  const displayedCards = useMemo(() => {
    const list = Array.isArray(cards) ? [...cards] : [];

    const dir = sortDir === "asc" ? 1 : -1;

    const num = (v) => {
      const n = Number(v);
      return Number.isFinite(n) ? n : 0;
    };

    const cooldownSecondsLeft = (card) => {
      const info = cardLimits?.[card.id];
      if (!info || info.loading || !info.resetAt)
        return Number.POSITIVE_INFINITY;
      const ts = new Date(info.resetAt).getTime();
      if (!Number.isFinite(ts)) return Number.POSITIVE_INFINITY;
      const diff = Math.ceil((ts - Date.now()) / 1000);
      return diff > 0 ? diff : 0;
    };

    list.sort((a, b) => {
      // ✅ NEW SORT: Availability (available first, cooldown last)
      if (sortKey === "availability") {
        const aLocked = isOnCooldown(a) ? 1 : 0;
        const bLocked = isOnCooldown(b) ? 1 : 0;

        // available first
        if (aLocked !== bLocked) return aLocked - bLocked;

        // if both locked, show the ones that come back sooner first
        if (aLocked === 1) {
          const da = cooldownSecondsLeft(a);
          const db = cooldownSecondsLeft(b);
          if (da !== db) return da - db;
        }

        // fallback: power desc (so it feels useful)
        return cardPower(b) - cardPower(a);
      }

      // Existing sorts
      if (sortKey === "level") return (num(a.level) - num(b.level)) * dir;
      if (sortKey === "wins") return (num(a.wins) - num(b.wins)) * dir;
      if (sortKey === "power") return (cardPower(a) - cardPower(b)) * dir;
      if (sortKey === "name")
        return String(a.name || "").localeCompare(String(b.name || "")) * dir;
      if (sortKey === "rarity")
        return (
          (rarityRank[normRarity(a.rarity)] -
            rarityRank[normRarity(b.rarity)]) *
          dir
        );

      return 0;
    });

    return list;
  }, [cards, sortKey, sortDir, rarityRank, cardLimits]);

  if (initialLoading) return <FancyLoader user={user} />;

  const fromArena = openedVia === "arena";

  const streakRibbonText = battleStreakInfo?.participating ? "ACTIVE" : "JOIN NOW";
const streakRibbonBg = battleStreakInfo?.participating
  ? "linear-gradient(180deg,#2ecc71,#1f9d55)"
  : "linear-gradient(180deg,#ffb300,#ff6f00)";

  return (
    <div
      className="fight-container"
      style={{
        backgroundImage: `url(${
          user?.side === "good" ? "/tap_bg_1.png" : "/fight_bg.png"
        })`,
        backgroundSize: "110%",
        backgroundRepeat: "repeat",
        backgroundPosition: "left -1px top",
      }}
    >

      {openedVia !== "menu" && (
        <>
          <h1 className="pagetitle">Choose Your Fighter</h1>
          <div
            style={{
              fontSize: 21,
              color: "#00BD4C",
              textAlign: "center",
              marginBottom: 20,
            }}
          >
            CURRENT PAYOUT:{" "}
            <span style={{ color: user?.side === "good" ? "#333" : "#F8BA1E" }}>
              {(100 + rewardPercent) / 100}X
            </span>
            <span style={{ color: "#fff", textShadow: "1px 1px #000" }}>
              &nbsp;| SHOWING <b>{cards.length}</b>/<b>{totalCards}</b>
            </span>
          </div>
        </>
      )}
      {openedVia === "menu" && <h1 className="pagetitle">Your Inventory</h1>}

      {/* Inventory Controls */}
      <div
        style={{
          width: "100%",
          maxWidth: 520,
          margin: "0 auto 14px",
          display: "flex",
          flexDirection: "column",
          gap: 12,
          alignItems: "center",
        }}
      >
        {/* Row 1 */}
        <div
          style={{ width: "100%", display: "flex", justifyContent: "center" }}
        >
          <div
            style={{
              width: "100%",
              maxWidth: 520,
              display: "flex",
              gap: 10,
              justifyContent: "center",
              boxSizing: "border-box",
            }}
          >
            <div style={{ flex: "0 1 50%", minWidth: 0 }}>
              <input
                value={nameQuery}
                onChange={(e) => setNameQuery(e.target.value)}
                placeholder="Search name…"
                style={{
                  height: 44,
                  width: "100%",
                  padding: "0 10px",
                  borderRadius: 10,
                  background: "#fff",
                  color: "#000",
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />
            </div>

            <div style={{ flex: "0 1 150px", minWidth: 0 }}>
              <FancySelect
                value={rarityFilter}
                onChange={setRarityFilter}
                options={rarityOptions}
                style={{ width: "100%", minWidth: 0, margin: 0 }}
              />
            </div>
          </div>
        </div>

        <div
          style={{
            marginTop: 0,
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 10,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 10,
              width: "100%",
            }}
          >
            <FancySelect
              value={sortKey}
              onChange={setSortKey}
              options={sortOptions}
              style={{ width: "100%", minWidth: 0, margin: 0 }}
            />

            <button
              type="button"
              onClick={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))}
              style={{ ...pillBtnStyle, width: 44, minWidth: 44, height: 44 }}
              title="Toggle sort direction"
            >
              <img
                src="/whitearrow.png"
                alt=""
                className={`fs-arrow ${sortDir === "asc" ? "is-open" : ""}`}
                style={{ width: 18, height: 18 }}
              />
            </button>

            <button
              type="button"
              disabled={isDefaultFilters}
              onClick={() => {
                setNameQuery("");
                setRarityFilter("all");
                setSortKey("availability");
                setSortDir("desc");
              }}
              style={{
                ...pillBtnStyle,
                width: 90,
                minWidth: 90,
                height: 44,
                padding: "0 12px",
                color: "#fff",
                opacity: isDefaultFilters ? 0.55 : 1,
                cursor: isDefaultFilters ? "not-allowed" : "pointer",
              }}
            >
              Reset
            </button>
          </div>
        </div>

{showBanner === 1 && battleStreakActive && (
  <div
    onClick={() => onNavigate?.("streakevent")}
    style={{
      width: "100%",
      maxWidth: 320,
      boxSizing: "border-box",
      margin: "10px auto",
      marginBottom: "0px",
      padding: "12px 16px",
      paddingLeft: "8px",
      borderRadius: 18,
      background: "linear-gradient(180deg,#9a6a3a,#6d3f1e)",
      border: "3px solid rgba(255,220,150,0.6)",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      position: "relative",
      overflow: "hidden",
    }}
  >

  {/* Ribbon */}
  <div
  style={{
    position: "absolute",
    top: 10,
    left: -40,
    width: 120,
    textAlign: "center",
    transform: "rotate(-45deg)",
    background: streakRibbonBg,
    color: "#fff",
    fontSize: 16,
    letterSpacing: 1,
    padding: "4px 0",
    boxShadow: "0 3px 6px rgba(0,0,0,0.4)",
    textShadow: "1px 1px 2px rgba(0,0,0,0.8)",
    pointerEvents: "none",
    zIndex: 2,
  }}
>
  {streakRibbonText}
</div>

  <img
    src="/winrate.png"
    alt=""
    style={{
      height: 34,
      filter: "drop-shadow(2px 2px 3px rgba(0,0,0,0.7))",
    }}
  />

  <div
    style={{
      flex: 1,
      textAlign: "center",
      color: "#fff",
      fontFamily: '"Mouse Memoirs", sans-serif',
      fontSize: "24px",
      textShadow: "2px 2px 3px rgba(0,0,0,0.8)",
      letterSpacing: 1,
      marginLeft: "-5px"
    }}
  >
    Rune Hunters
  </div>

  <div
    style={{
      padding: "6px 14px",
      borderRadius: 999,
      background: "rgba(0,0,0,0.35)",
      border: "2px solid rgba(255,220,150,0.6)",
      color: "#FFD27A",
      fontFamily: '"Mouse Memoirs", sans-serif',
      fontSize: 18,
      letterSpacing: 1,
      textShadow: "1px 1px 2px rgba(0,0,0,0.8)",
      whiteSpace: "nowrap",
    }}
  >
    ENTER
  </div>

</div>
)}

        {/* Showing */}
        <div
          style={{
            display: "none",
            color: user?.side === "good" ? "#000" : "#fff",
            fontSize: 24,
            width: "100%",
            textAlign: "center",
            marginTop: 5,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            minWidth: 0,
          }}
        >
          Showing <b>{cards.length}</b>/<b>{totalCards}</b>
        </div>
      </div>

      <div className="card-grid">
        {!loading && cards.length === 0 && (
          <div
            style={{
              textAlign: "center",
              color: "#fff",
              textShadow: "1px 1px #000",
              fontWeight: "normal",
              marginBottom: "25px",
            }}
          >
            <div
              style={{
                color: `${user?.side === "good" ? "#000" : "#fff"}`,
                textShadow: "0px 0px",
                fontWeight: "normal",
                opacity: 0.85,
                marginBottom: 14,
              }}
            >
              You don’t have any fighters yet.
              <br />
              Visit the shop to get your first one!
            </div>
          </div>
        )}

        {openedVia !== "menu" && (
          <div className="card-item">
            <div className="card-title" style={{ textAlign: "center" }}>
              NEW FIGHTER
            </div>
            <div
              className="character-preview"
              style={{
                position: "relative",
                backgroundImage: "url('/new_bg.png')",
                backgroundSize: "cover",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "left center",
                textAlign: "center",
                backgroundColor: "#73492f",
              }}
            >
              <div className="card-stats"></div>
              <div
                className="card-thumb"
                style={{ textAlign: "center", height: "calc(100% + 32px)" }}
              >
                <img
                  src="/new.png"
                  style={{
                    marginTop: "-10px",
                    position: "relative",
                    width: "auto",
                  }}
                  alt="new"
                />
              </div>
            </div>
            <div
              className="bts"
              onClick={() => onNavigate("shop")}
              style={{
                fontSize: "21px",
                width: "100%",
                backgroundImage: "url('/buy.png')",
                backgroundSize: "100% 42px",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "left top",
              }}
            >
              SHOP
            </div>
          </div>
        )}

        {displayedCards.map((card, index) => {
          const disabled =
            evolvingId === card.id || (card.level ?? 1) >= MAX_LEVEL;

          const price = getEvolveOptions(card);
          const fightReward = fightRewardFor(card.rarity);
          const abilitiesSafe = sanitizeAbilityList(
            parseAbilities(card.abilities),
          );

          const limitForCard = cardLimits[card.id];
          const cardLimitLocked =
            limitForCard &&
            !limitForCard.loading &&
            limitForCard.used >= limitForCard.limit;
          const cardCooldown = cardCooldowns[card.id];

          return (
            <div key={card.id} className="card-item">
              <div className="card-title">
                {truncateCardName(card.name)}
                {nameChangePrice > 0 && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      openRename(card);
                    }}
                    style={{
                      width: 22,
                      height: 22,
                      border: "none",
                      cursor: "pointer",
                      backgroundColor: "transparent",
                      padding: 0,
                      marginLeft: 0,
                    }}
                    title="Rename card"
                  >
                    <img
                      src="/edit.png"
                      style={{ height: "24px", verticalAlign: "middle" }}
                    />
                  </button>
                )}

                <span
                  style={{
                    backgroundImage: "url('/wins.png')",
                    backgroundSize: "28px",
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "left center",
                  }}
                >
                  {card.wins || 0}
                </span>
              </div>

              <div
                className="character-preview"
                onClick={() => {
                  persistSelectedCard(card);

                  if (openedVia === "arena") {
                    onNavigate("arenas", { cardId: card.id });
                  } else {
                    onNavigate("character");
                  }
                }}
              >
                <div
                  className="card-number"
                  style={{
                    backgroundImage: "url('/number.png')",
                    backgroundSize: "36px",
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "left center",
                  }}
                >
                  {card.level}
                </div>

                {card.for_sale && (
                  <div className="for-sale-badge">FOR SALE</div>
                )}

                <div
                  className="card-type"
                  style={{
                    backgroundImage:
                      "url('/arrow2.png'), url('/card-type.png')",
                    backgroundSize: "10px 10px,100% 24px",
                    backgroundRepeat: "no-repeat, no-repeat",
                    backgroundPosition: "left 6px top 6px, left center",
                  }}
                >
                  {card.rarity}
                </div>

                <div className="card-stats">
                  <div
                    style={{
                      backgroundImage: "url('/s_attack.png')",
                      backgroundSize: "15px",
                      backgroundRepeat: "no-repeat",
                      backgroundPosition: "left center",
                    }}
                  >
                    {card.attack}
                  </div>
                  <div
                    style={{
                      paddingLeft: "18px",
                      backgroundImage: "url('/s_defense.png')",
                      backgroundSize: "15px",
                      backgroundRepeat: "no-repeat",
                      backgroundPosition: "left center",
                    }}
                  >
                    {card.defense}
                  </div>
                  <div
                    style={{
                      paddingLeft: "18px",
                      backgroundImage: "url('/s_health.png')",
                      backgroundSize: "15px",
                      backgroundRepeat: "no-repeat",
                      backgroundPosition: "left center",
                    }}
                  >
                    {card.health}
                  </div>
                </div>

               {abilitiesSafe.map((ability, idx) => {
  const abilityKey = ability.toLowerCase();

  const isHintTarget =
    ABILITY_CHANGE_HINT_ENABLED &&
    showAbilityHint &&
    Number(card.id) === Number(hintCardId) &&
    idx === hintAbilityIndex;

  return (
    <div
      key={idx}
      className={`abl_${idx + 1}`}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();

        if (isHintTarget) {
          dismissAbilityHintForever();
        }

        openAbilityChange(card, ability);
      }}
      title={`Change ${ability}`}
      style={{
        width: "32px",
        height: "32px",
        backgroundImage: "url('/abl.png')",
        backgroundSize: "32px",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center center",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        zIndex: isHintTarget ? 60 : 30,
        position: "absolute",
        borderRadius: "50%",
        boxShadow: isHintTarget
          ? "0 0 0 4px rgba(255,223,95,0.22), 0 0 18px rgba(255,223,95,0.95)"
          : "none",
        animation: isHintTarget
          ? "abilityHintPulse 1.4s ease-in-out infinite"
          : "none",
      }}
    >
      <img
        src={`/a_${abilityKey}.png`}
        alt={ability}
        loading="lazy"
        decoding="async"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();

          if (isHintTarget) {
            dismissAbilityHintForever();
          }

          openAbilityChange(card, ability);
        }}
        style={{
          width: "24px",
          height: "24px",
          pointerEvents: "auto",
        }}
      />

      {isHintTarget && (
        <div
          style={{
            position: "absolute",
            right: -8,
            bottom: "calc(100% + 10px)",
            width: 100,
            maxWidth: "calc(100vw - 32px)",
            padding: "8px 10px",
            borderRadius: 10,
            background: "rgba(35, 20, 10, 0.96)",
            color: "#fff7d6",
            fontSize: 14,
            fontWeight: 300,
            lineHeight: 1.2,
            textAlign: "center",
            boxShadow: "0 6px 16px rgba(0,0,0,0.45)",
            border: "1px solid rgba(255,223,95,0.45)",
            pointerEvents: "none",
            textTransform: "none",
            whiteSpace: "normal",
            zIndex: 999,
          }}
        >
          Tap to Change Ability
          <div
            style={{
              position: "absolute",
              right: 15,
              top: "100%",
              width: 0,
              height: 0,
              borderLeft: "7px solid transparent",
              borderRight: "7px solid transparent",
              borderTop: "8px solid rgba(35, 20, 10, 0.96)",
            }}
          />
        </div>
      )}
    </div>
  );
})}

                {/* Card Pieces */}
{Number(card.pieces) > 0 && (() => {
  const canConvertPieces =
    Number(card.level || 1) === MAX_LEVEL &&
    Number(card.pieces || 0) > 0;

  return (
    <div
      className="card-pieces"
      role={canConvertPieces ? "button" : undefined}
      tabIndex={canConvertPieces ? 0 : undefined}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();

        if (canConvertPieces) {
          openPieceConvertConfirm(card);
        }
      }}
      onKeyDown={(e) => {
        if (!canConvertPieces) return;

        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          openPieceConvertConfirm(card);
        }
      }}
      style={{
        position: "absolute",
        top: 38,
        left: 8,
        padding: "4px 4px",
        borderRadius: 8,
        fontSize: 13,
        fontWeight: "bold",
        color: "#fff",
        background: canConvertPieces
          ? "rgba(248,186,30,0.85)"
          : "rgba(0,0,0,0.65)",
        border: canConvertPieces
          ? "1px solid rgba(255,255,255,0.55)"
          : "1px solid rgba(255,255,255,0.25)",
        display: "flex",
        alignItems: "center",
        gap: 4,
        textShadow: "1px 1px #000",
        zIndex: 45,
        cursor: canConvertPieces ? "pointer" : "default",
        boxShadow: canConvertPieces
          ? "0 0 10px rgba(248,186,30,0.45)"
          : "none",
      }}
      title={
        canConvertPieces
          ? "Convert card pieces into Sparks"
          : "Card pieces"
      }
    >
      <img
        src="/pieces.png"
        alt="Pieces"
        style={{ width: 14, height: 14 }}
      />
      {card.pieces}
    </div>
  );
})()}

               <div
  className="card-thumb"
  style={{
    position: "relative",
  }}
>
  {Object.entries(getCharacterObj(card) || {})
    .sort(([a], [b]) => {
      const order = ["background", "body", "head"];
      const indexA = order.includes(a) ? order.indexOf(a) : order.length;
      const indexB = order.includes(b) ? order.indexOf(b) : order.length;
      return indexA - indexB;
    })
    .map(([part, data]) => {
      const d = data && typeof data === "object" ? data : {};
      const defaultSrc = `/assets/${part}/${d.side}/${d.rarity}/${d.variant}_thumb.png`;

      const seasonName =
        card?.seasonal_accessory?.season_name ||
        card?.season_name ||
        null;

      const src = getSkinSrc(seasonName, part, defaultSrc);

      const zIndexMap = {
        background: 1,
        body: 2,
        head: 11,
      };

      return (
        <img
          key={part}
          src={src}
          alt={part}
          loading="lazy"
          decoding="async"
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "contain",
            zIndex: zIndexMap[part] ?? 10,
            pointerEvents: "none",
          }}
        />
      );
    })}
</div>
              </div>

              {openedVia !== "menu" && (
                <div
                  className="bts"
                  onClick={() => !disabled && openEvolveConfirm(card)}
                  style={{
                    backgroundImage: "url('/evolve.png')",
                    backgroundSize: "100% 42px",
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "left top",
                    opacity: disabled ? 0.4 : 1,
                    pointerEvents: disabled ? "none" : "auto",
                    filter: disabled ? "grayscale(70%)" : "none",
                  }}
                  title={
                    (card.level ?? 1) >= MAX_LEVEL
                      ? "Max level reached"
                      : undefined
                  }
                >
                 <span>EVOLVE</span>

<span
  style={{
    display: "flex",
    gap: 4,
    alignItems: "center",
    justifyContent: "center",
    marginLeft: 0,
  }}
>
  {Number(price.options?.sparks || 0) > 0 && (
    <img
      src="/sparks.png"
      alt="Sparks"
      style={{ width: 16, height: 16, objectFit: "contain" }}
    />
  )}

  {Number(price.options?.runes || 0) > 0 && (
    <img
      src="/runes.png"
      alt="Runes"
      style={{ width: 16, height: 16, objectFit: "contain" }}
    />
  )}

  {Number(price.options?.cardPieces || 0) > 0 && (
    <img
      src="/pieces.png"
      alt="Pieces"
      style={{ width: 16, height: 16, objectFit: "contain" }}
    />
  )}
</span>
                </div>
              )}

              {openedVia === "menu" && (
                <div
                  className="bts"
                  onClick={() => !disabled && openEvolveConfirm(card)}
                  style={{
                    flexDirection: "row",
                    width: "100%",
                    backgroundImage: "url('/evolve.png')",
                    backgroundSize: "100% 42px",
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "left top",
                    opacity: disabled ? 0.4 : 1,
                    pointerEvents: disabled ? "none" : "auto",
                    filter: disabled ? "grayscale(70%)" : "none",
                  }}
                  title={
                    (card.level ?? 1) >= MAX_LEVEL
                      ? "Max level reached"
                      : undefined
                  }
                >
                 <span>EVOLVE</span>

{price.isLegacy ? (
  <span
    style={{
      marginLeft: "6px",
      paddingLeft: "18px",
      backgroundImage: "url('/sparks.png')",
      backgroundSize: "16px",
      backgroundRepeat: "no-repeat",
      backgroundPosition: "left center",
    }}
  >
    {Number(price.options?.sparks || 0)}
  </span>
) : (
  <span style={{ display: "flex", gap: 3, alignItems: "center", marginLeft: 6 }}>
    {Number(price.options?.runes || 0) > 0 && (
      <span
        style={{
          paddingLeft: "18px",
          backgroundImage: "url('/runes.png')",
          backgroundSize: "16px",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "left center",
        }}
      >
        {Number(price.options?.runes || 0)}
      </span>
    )}

    {Number(price.options?.cardPieces || 0) > 0 && (
      <span
        style={{
          paddingLeft: "18px",
          backgroundImage: "url('/evolveicon.png')",
          backgroundSize: "16px",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "left center",
        }}
      >
        {Number(price.options?.cardPieces || 0)}
      </span>
    )}
  </span>
)}
                </div>
              )}

              {/* CHANGED: FIGHT now opens hype modal instead of navigating */}
              {openedVia !== "menu" && (
                <div
                  onClick={() => {
                   if (fromArena) {
                      persistSelectedCard(card);
                      onNavigate("arenas", { cardId: card.id });
                      return;
                    }

                    if (cardLimitLocked) {
                      setRefillCard(card);
                      setFightRefillError("");
                      setShowFightRefillModal(true);
                      return;
                    }

                    setPendingCard(card);
                    setBattleSoonOpen(true);
                  }}
                  className="bts"
                  style={{
                    opacity: cardLimitLocked ? 0.6 : fightReward > 0 ? 1 : 0.4,
                    backgroundImage: "url('/fight.png')",
                    backgroundSize: "100% 42px",
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "left top",
                    cursor: "pointer",
                  }}
                  title={
                    cardLimitLocked
                      ? cardCooldown
                        ? `Next battle in ${cardCooldown} · tap to buy more fights or wait for reset.`
                        : "Daily limit reached for this card · tap to buy more fights or wait for reset."
                      : fightReward > 0
                      ? `Reward: ${fightReward} Sparks`
                      : "No reward set in CMS"
                  }
                >
                  {cardLimitLocked ? (
                    <>
                      <span style={{ fontSize: 11, letterSpacing: 0.5 }}>
                        BUY FIGHTS OR WAIT
                      </span>
                      <span
                        style={{
                          paddingLeft: 0,
                          backgroundImage: "none",
                          fontSize: 13,
                        }}
                      >
                        {cardCooldown || "--:--:--"}
                      </span>
                    </>
                  ) : (
                    <>
                      <span>
                        {fromArena ? (
                          <>
                            ENTER<br />
                            THE ARENA
                          </>
                        ) : (
                          "FIGHT"
                        )}
                      </span>
                      {!fromArena && (
                        <span
                          style={{
                            paddingLeft: "18px",
                            backgroundImage: "url('/sparks.png')",
                            backgroundSize: "16px",
                            backgroundRepeat: "no-repeat",
                            backgroundPosition: "left center",
                          }}
                        >
                          +{fightReward}
                        </span>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {(totalCards ?? 0) > PAGE_SIZE && (
          <div
            style={{
              display: "flex",
              marginTop: 15,
              paddingTop: 15,
              alignItems: "center",
              justifyContent: "center",
              gap: 10,
              width: "100%",
              flexWrap: "wrap",
            }}
          >
            {/* First */}
            <button
              aria-label="First page"
              disabled={page <= 1}
              onClick={() => setPage(1)}
              style={{
                backgroundColor: page <= 1 ? "#444" : "#ffcc62",
                color: page <= 1 ? "#888" : "#000",
                border: "none",
                borderRadius: 10,
                width: 38,
                height: 38,
                fontSize: 18,
                cursor: page <= 1 ? "not-allowed" : "pointer",
                boxShadow: page <= 1 ? "none" : "0 0 6px #ffcc62",
                transition: "transform 0.15s ease",
              }}
              onMouseEnter={(e) => {
                if (page > 1) e.currentTarget.style.transform = "scale(1.1)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "scale(1)";
              }}
            >
              ⏮
            </button>

            {/* Prev */}
            <button
              aria-label="Previous page"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              style={{
                backgroundColor: page <= 1 ? "#444" : "#ffcc62",
                color: page <= 1 ? "#888" : "#000",
                border: "none",
                borderRadius: 10,
                width: 38,
                height: 38,
                fontSize: 18,
                cursor: page <= 1 ? "not-allowed" : "pointer",
                boxShadow: page <= 1 ? "none" : "0 0 6px #ffcc62",
                transition: "transform 0.15s ease",
              }}
              onMouseEnter={(e) => {
                if (page > 1) e.currentTarget.style.transform = "scale(1.1)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "scale(1)";
              }}
            >
              ◀
            </button>

            {/* Center label */}
            <span
              style={{
                color: "#ffcc62",
                textShadow: "1px 1px #000",
                fontSize: 17,
                minWidth: 120,
                textAlign: "center",
                padding: "6px 10px",
                borderRadius: 10,
                background: "rgba(0,0,0,0.35)",
                border: "1px solid #555",
                margin: "0 2px",
              }}
            >
              Page {page} / {totalPages ?? "…"}
            </span>

            {/* Next */}
            <button
              aria-label="Next page"
              disabled={!pagesReady || page >= totalPages}
              onClick={() => {
                if (!pagesReady) return;
                setPage((p) => Math.min(totalPages, p + 1));
              }}
              style={{
                backgroundColor:
                  !pagesReady || page >= totalPages ? "#444" : "#ffcc62",
                color: !pagesReady || page >= totalPages ? "#888" : "#000",
                border: "none",
                borderRadius: 10,
                width: 38,
                height: 38,
                fontSize: 18,
                cursor:
                  !pagesReady || page >= totalPages ? "not-allowed" : "pointer",
                boxShadow:
                  !pagesReady || page >= totalPages
                    ? "none"
                    : "0 0 6px #ffcc62",
                transition: "transform 0.15s ease",
              }}
              onMouseEnter={(e) => {
                if (pagesReady && page < totalPages)
                  e.currentTarget.style.transform = "scale(1.1)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "scale(1)";
              }}
            >
              ▶
            </button>

            {/* Last */}
            <button
              aria-label="Last page"
              disabled={!pagesReady || page >= totalPages}
              onClick={() => {
                if (!pagesReady) return;
                setPage(totalPages);
              }}
              style={{
                backgroundColor:
                  !pagesReady || page >= totalPages ? "#444" : "#ffcc62",
                color: !pagesReady || page >= totalPages ? "#888" : "#000",
                border: "none",
                borderRadius: 10,
                width: 38,
                height: 38,
                fontSize: 18,
                cursor:
                  !pagesReady || page >= totalPages ? "not-allowed" : "pointer",
                boxShadow:
                  !pagesReady || page >= totalPages
                    ? "none"
                    : "0 0 6px #ffcc62",
                transition: "transform 0.15s ease",
              }}
              onMouseEnter={(e) => {
                if (pagesReady && page < totalPages)
                  e.currentTarget.style.transform = "scale(1.1)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "scale(1)";
              }}
            >
              ⏭
            </button>
          </div>
        )}
      </div>

      {/* EXISTING: EVOLVE MODAL */}
      <AnimatePresence>
        {evolveOpen && evolveData && (
          <motion.div
            className="modal-backdrop no-css-anim"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{ backdropFilter: "blur(2px)" }}
          >
            <motion.div
              className="modal fancy-success-modal"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", stiffness: 260, damping: 22 }}
              style={{
                textAlign: "center",
                position: "relative",
                overflow: "hidden",
              }}
            >
              {[...Array(16)].map((_, i) => (
                <motion.span
                  key={i}
                  initial={{ opacity: 0, scale: 0.3 }}
                  animate={{
                    opacity: [0, 1, 0],
                    scale: [0.3, 1.1, 0.8],
                    x: Math.cos((i / 16) * Math.PI * 2) * 140,
                    y: Math.sin((i / 16) * Math.PI * 2) * 140,
                  }}
                  transition={{
                    duration: 1.2,
                    delay: 0.05 * i,
                    ease: "easeOut",
                  }}
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    background: "linear-gradient(#fff,#ffd700)",
                    pointerEvents: "none",
                  }}
                />
              ))}

              <h1 className="pagetitle" style={{ color: "#ffdf5f" }}>
                {evolveData.name} Evolved!
              </h1>
              <div
                style={{
                  textShadow: "1px 1px #000",
                  fontSize: "24px",
                  color: "#9adcfb",
                  marginBottom: 12,
                }}
              >
                {evolveData.rarity}
              </div>

              <div
                style={{
                  float: "left",
                  width: "100%",
                  textAlign: "center",
                  display: "block",
                  fontSize: "18px",
                  margin: "12px 0",
                }}
              >
                {["attack", "defense", "health"].map((key) => {
                  const oldVal = evolveData.oldStats[key];
                  const newVal = evolveData.newStats[key];
                  const delta = newVal - oldVal;
                  return (
                    <div
                      key={key}
                      style={{
                        display: "inline-block",
                        textAlign: "left",
                        marginBottom: "10px",
                      }}
                    >
                      <div
                        style={{
                          width: "80px",
                          float: "left",
                          textTransform: "capitalize",
                          color: "#ddd",
                        }}
                      >
                        <img
                          src={`/s_${key}.png`}
                          alt="→"
                          style={{
                            width: "18px",
                            marginTop: "-1px",
                            marginRight: "10px",
                            verticalAlign: "middle",
                          }}
                        />
                        {key}:
                      </div>
                      <div
                        style={{
                          float: "left",
                          textAlign: "right",
                          color: "#bbb",
                        }}
                      >
                        <div
                          style={{
                            textAlign: "center",
                            float: "left",
                            width: "25px",
                          }}
                        >
                          {oldVal}
                        </div>
                        <div
                          style={{
                            textAlign: "center",
                            float: "left",
                            width: "20px",
                          }}
                        >
                          →
                        </div>
                        <div
                          style={{
                            textAlign: "center",
                            float: "left",
                            width: "50px",
                          }}
                        >
                          {newVal} ({delta >= 0 ? `+${delta}` : delta})
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="modal-footer" style={{ marginTop: 12 }}>
                <button
                  onClick={() => {
                    setEvolveOpen(false);
                    setEvolveData(null);
                  }}
                >
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* NEW: BATTLE “COMING SOON” MODAL */}
      <AnimatePresence>
        {battleSoonOpen && (
          <motion.div
            className="modal-backdrop no-css-anim"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{ backdropFilter: "blur(2px)" }}
          >
            <motion.div
              className="modal fancy-success-modal"
              initial={{ scale: 0.85, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ type: "spring", stiffness: 260, damping: 22 }}
              style={{
                textAlign: "center",
                position: "relative",
                overflow: "hidden",
              }}
            >
              {/* Sparkle ring */}
              {[...Array(18)].map((_, i) => (
                <motion.span
                  key={i}
                  initial={{ opacity: 0, scale: 0.4 }}
                  animate={{
                    opacity: [0, 1, 0],
                    scale: [0.4, 1.2, 0.9],
                    x: Math.cos((i / 18) * Math.PI * 2) * 160,
                    y: Math.sin((i / 18) * Math.PI * 2) * 160,
                  }}
                  transition={{
                    duration: 1.4,
                    delay: 0.05 * i,
                    ease: "easeOut",
                  }}
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    background: "linear-gradient(#ffdf5f,#9adcfb)",
                    pointerEvents: "none",
                  }}
                />
              ))}

              <h1
                className="pagetitle"
                style={{ color: "#ffdf5f", marginBottom: 6 }}
              >
                Ready to Rumble?
              </h1>

              <div style={{ marginTop: 2, marginBottom: 10 }}>
                <span
                  style={{
                    display: "inline-block",
                    padding: "4px 10px",
                    borderRadius: 999,
                    fontSize: 14,
                    fontWeight: 700,
                    letterSpacing: 0.5,
                    background: "rgba(0,0,0,0.5)",
                    border: "1px solid rgba(255,255,255,0.25)",
                    color: "#ffdf5f",
                    textShadow: "0 1px 2px #000",
                  }}
                >
                  {limitInfo.loading
                    ? "Checking battles…"
                    : limitReached
                    ? `LIMIT REACHED ${limitInfo.used} / ${limitInfo.limit}${
                        cooldownLabel ? ` · next reset in ${cooldownLabel}` : ""
                      }`
                    : `DAILY BATTLE ${nextBattle} / ${limitInfo.limit}`}
                </span>
              </div>

              <div
                style={{
                  color: "#9adcfb",
                  textShadow: "1px 1px #000",
                  fontSize: 22,
                  marginBottom: 6,
                  marginTop: 12,
                }}
              >
                {tagline}
              </div>

              <div
                style={{
                  fontSize: 20,
                  color: "#ddd",
                  opacity: 0.95,
                  marginBottom: 16,
                  lineHeight: 1.35,
                }}
              >
                {bulletLines.map((line, i) => (
                  <div key={i}>{line}</div>
                ))}
              </div>

              {feeLoading ? (
                "Calculating payout…"
              ) : (
                <>
                  Win Payout: <strong>{projectedWin} Sparks</strong>
                </>
              )}

              {/* ----- FOOTER: buttons on separate centered lines ----- */}
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 }}
                className="modal-footer"
                style={{
                  gap: 10,
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: "center",
                  marginTop: 10,
                }}
              >
                {/* --- Battle streak panel --- */}
                {(() => {
                  const {
                    loading,
                    currentLevel,
                    active,
                    progressInCycle,
                    cycleSize,
                    prevMilestone,
                    nextMilestone,
                    remaining,
                  } = battleStreakInfo;

                  if (loading) return null;
                  if (!active) return null;

                  const segmentStart = prevMilestone ?? 0;
                  const segmentEnd = nextMilestone ?? segmentStart;
                  const denom = Math.max(1, segmentEnd - segmentStart);

                  const p =
                    loading || cycleSize <= 0
                      ? 0
                      : nextMilestone == null
                      ? 1
                      : clamp01((progressInCycle - segmentStart) / denom);

                  return (
                    <div
                      style={{
                        width: "100%",
                        background: "rgba(0,0,0,0.35)",
                        border: "1px solid rgba(255,255,255,0.12)",
                        borderRadius: 12,
                        padding: "10px 12px",
                        marginBottom: 0,
                        textShadow: "1px 1px #000",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "baseline",
                        }}
                      >
                        <div style={{ fontSize: 16, color: "#ddd" }}>
                          🏆 Rune Hunters:{" "}
                          <b style={{ color: "#ffbb01" }}>
                            {loading
                              ? "…"
                              : `Level ${battleStreakInfo.currentLevel}`}
                          </b>
                        </div>

                        <div style={{ fontSize: 16, color: "#bbb" }}>
                          {loading ? "Loading…" : `Next Reward In ${remaining}`}{" "}
                          Wins
                        </div>
                      </div>

                      <div
                        style={{
                          height: 8,
                          background: "rgba(255,255,255,0.08)",
                          borderRadius: 999,
                          overflow: "hidden",
                          marginTop: 8,
                        }}
                      >
                        <div
                          style={{
                            height: "100%",
                            width: `${Math.round(p * 100)}%`,
                            background: "rgba(255,187,1,0.75)",
                          }}
                        />
                      </div>
                    </div>
                  );
                })()}

                {/* Line 1: centered Enter button */}
                <div
                  style={{
                    flexBasis: "100%",
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <SparkGuardButton
                    ref={enterBtnRef}
                    user={user}
                    hintText="Not enough Sparks"
                    disabled={enterDisabled}
                    title={
                      !limitInfo.ok
                        ? "Checking battle limits…"
                        : limitReached
                        ? "Daily limit reached for this card"
                        : undefined
                    }
                    style={{
                      minWidth: 140,
                      opacity: enterDisabled ? 0.7 : 1,
                      cursor: enterDisabled ? "not-allowed" : "pointer",
                    }}
                    onConfirm={async () => {
                      if (!limitInfo.ok) return;
                      if (!pendingCard) return;
                      if (limitReached) {
                        setErrorMessage(
                          `Daily battle limit reached (${limitInfo.used}/${limitInfo.limit}).`,
                        );
                        setShowErrorModal(true);
                        return;
                      }

                      const have = Number(user?.sparks ?? 0);
                      const need = Number(entryFee ?? 0);
                      if (Number.isFinite(need) && have < need) {
                        setErrorMessage(
                          `You need ${need} SPARKS to start, but you only have ${have}.`,
                        );
                        setShowErrorModal(true);
                        enterBtnRef.current?.showHint?.();
                        return;
                      }

                      try {
                        setEntering(true);
                        const res = await fetch(
                          `${process.env.REACT_APP_API_URL}/api/battle/bf8c3a/starta`,
                          {
                            method: "POST",
                            headers: {
                              "Content-Type": "application/json",
                              Authorization: `Bearer ${localStorage.getItem(
                                "token",
                              )}`,
                              "X-TG-INITDATA":
                                window.Telegram?.WebApp?.initData || "",
                            },
                            body: JSON.stringify({ cardId: pendingCard.id }),
                          },
                        );

                        const data = await res.json().catch(() => ({}));
                        if (!res.ok) {
                          const msg = String(data?.error || "").toLowerCase();

                          const code = String(data?.error || "");

                          if (code === "HUMAN_CHECK_REQUIRED") {
                            setBattleSoonOpen(false);
                            setPendingCard(null);
                            setEntering(false);

                            onNavigate("humancheck", {
                              returnTo: "fight",
                              returnParams: {
                                openedVia,
                              },
                            });

                            return;
                          }

                          if (code === "DEFENSE_DECK_REQUIRED") {
                            openDefenseDeckRequiredModal();
                            return;
                          }

                          if (
                            data?.error === "DAILY_LIMIT" ||
                            msg.includes("daily_limit")
                          ) {
                            setErrorMessage(
                              `Daily battle limit reached (${
                                data?.used ?? "?"
                              }/${data?.limit ?? "?"}).`,
                            );
                            setShowErrorModal(true);
                            return;
                          }
                          if (
                            res.status === 402 ||
                            msg.includes("insufficient") ||
                            msg.includes("no funds") ||
                            msg.includes("spark")
                          ) {
                            setErrorMessage(
                              data?.error || "Not enough SPARKS.",
                            );
                            setShowErrorModal(true);
                            enterBtnRef.current?.showHint?.();
                            return;
                          }

                          if (!res.ok) {
                            const code = String(data?.error || "");
                            const msg = String(code).toLowerCase();

                              if (code === "DEFENSE_DECK_REQUIRED") {
                                openDefenseDeckRequiredModal();
                                return;
                              }

                            if (
                              res.status === 429 &&
                              (code === "COOLDOWN" || msg.includes("cooldown"))
                            ) {
                              const retryIn = Number(data?.retryIn ?? 0);
                              const funny =
                                data?.message ||
                                `Too fast 😅 Try again in ${retryIn}s.`;

                              setErrorMessage(funny);
                              setShowErrorModal(true);
                              return;
                            }

                            // DAILY LIMIT (existing)
                            if (
                              data?.error === "DAILY_LIMIT" ||
                              msg.includes("daily_limit")
                            ) {
                              setErrorMessage(
                                `Daily battle limit reached (${
                                  data?.used ?? "?"
                                }/${data?.limit ?? "?"}).`,
                              );
                              setShowErrorModal(true);
                              return;
                            }

                            // INSUFFICIENT SPARKS (existing)
                            if (
                              res.status === 402 ||
                              msg.includes("insufficient") ||
                              msg.includes("no funds") ||
                              msg.includes("spark")
                            ) {
                              setErrorMessage(
                                data?.error || "Not enough SPARKS.",
                              );
                              setShowErrorModal(true);
                              enterBtnRef.current?.showHint?.();
                              return;
                            }

                            // fallback
                            throw new Error(
                              data?.message ||
                                data?.error ||
                                "Unable to start battle",
                            );
                          }

                          throw new Error(
                            data?.error || "Unable to start battle",
                          );
                        }

                        if (data.user && typeof setUser === "function") {
                          setUser((u) => ({
                            ...(u || {}),
                            ...(data.user || {}),
                          }));
                        }

                        persistSelectedCard(pendingCard);
                        setBattleSoonOpen(false);

                        posthog?.capture("battle_entered", {
                          battle_id: data.battleId,
                          card_id: pendingCard.id,
                          card_name: pendingCard.name,
                          card_rarity: pendingCard.rarity,
                          entry_fee: entryFee,
                          user_level: user?.level,
                          opened_via: openedVia,
                        });

                        onNavigate("battle", {
                          battleId: data.battleId,
                          cardId: pendingCard.id,
                        });
                      } catch (err) {
                        const m = String(err?.message || "").toLowerCase();
                        if (
                          m.includes("insufficient") ||
                          m.includes("no funds") ||
                          m.includes("spark")
                        ) {
                          enterBtnRef.current?.showHint?.();
                        }
                        setErrorMessage(err.message || "Transaction failed");
                        setShowErrorModal(true);
                      } finally {
                        setEntering(false);
                      }
                    }}
                  >
                    {entering
                      ? "Summoning Arena…"
                      : feeLoading
                      ? "Counting Sparks…"
                      : !limitInfo.ok
                      ? "Checking battles…"
                      : limitReached
                      ? `Limit Reached (${limitInfo.used}/${limitInfo.limit})`
                      : `Enter Battle ${nextBattle}/${limitInfo.limit} (${entryFee} Sparks)`}
                  </SparkGuardButton>
                </div>

                <button
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();

                    setBattleSoonOpen(false);

                    setPendingCard(null);

                    setFeeLoading(true);
                  }}
                  style={{
                    minWidth: 120,
                    marginTop: 0,
                    alignSelf: "center",
                    flexBasis: "100%",
                    maxWidth: 160,
                    marginLeft: "auto",
                    marginRight: "auto",
                  }}
                >
                  Not Today 😅
                </button>
              </motion.div>
              {/* ----- /FOOTER ----- */}

              <div
                style={{
                  fontSize: 18,
                  color: "#ffdf5f",
                  textShadow: "1px 1px #000",
                  marginBottom: 8,
                }}
              />

              <div
                style={{
                  marginTop: 14,
                  fontSize: 15,
                  color: "#bbb",
                  fontWeight: "bold",
                }}
              >
                Pro tip: Evolve cards → bigger numbers → bigger flex.
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* BUY MORE FIGHTS MODAL */}
      {showFightRefillModal && refillCard && (
        <div className="modal-backdrop">
          <motion.div
            className="modal fancy-success-modal"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 300, damping: 20 }}
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src="/energy.png"
              alt="Out of fights"
              style={{ height: 72, marginBottom: 6 }}
            />
            <h2
              style={{
                color: "#FFD54F",
                marginBottom: 8,
                textShadow: "1px 1px #000",
              }}
            >
              Out of Fights
            </h2>
            <p
              style={{
                fontSize: 18,
                color: "#ddd",
                textShadow: "1px 1px #000",
                margin: "0 0 12px",
              }}
            >
              Choose how you want to get more fights for this card.
            </p>
            <div
              style={{
                display: "grid",
                gap: 8,
                margin: "8px auto 4px",
                width: "100%",
              }}
            >
              <button
                type="button"
                disabled={!!fightRefillLoading}
                onClick={handleFightRefillVideo}
                style={{
                  padding: "10px 12px",
                  borderRadius: 10,
                  border: "none",
                  background:
                    "linear-gradient(90deg, #4FC3F7, #29B6F6, #0288D1)",
                  color: "#fff",
                  fontSize: 16,
                  fontWeight: 600,
                  cursor: fightRefillLoading ? "not-allowed" : "pointer",
                  boxShadow: "0 4px 10px rgba(0,0,0,0.4)",
                }}
              >
                {fightRefillLoading === "video"
                  ? "Loading video..."
                  : `Watch video (+${FIGHT_REFILL_VIDEO_FIGHTS} fight)${
                      FIGHT_REFILL_VIDEO_FIGHTS > 1 ? "s" : ""
                    }`}
              </button>
              <button
                type="button"
                disabled={
                  !!fightRefillLoading ||
                  !(
                    typeof window !== "undefined" &&
                    window.Telegram?.WebApp?.openInvoice
                  )
                }
                onClick={handleFightRefillStars}
                style={{
                  padding: "10px 12px",
                  borderRadius: 10,
                  border: "none",
                  background:
                    "linear-gradient(90deg, #FFB300, #FF8F00, #F57C00)",
                  color:
                    !!fightRefillLoading ||
                    !(
                      typeof window !== "undefined" &&
                      window.Telegram?.WebApp?.openInvoice
                    )
                      ? "#555"
                      : "#fff",
                  fontSize: 16,
                  fontWeight: 600,
                  cursor: fightRefillLoading ? "not-allowed" : "pointer",
                  boxShadow: "0 4px 10px rgba(0,0,0,0.4)",
                }}
              >
                {fightRefillLoading === "stars"
                  ? "Starting payment..."
                  : `Buy ${FIGHT_REFILL_STARS_FIGHTS} fights for ${FIGHT_REFILL_STARS_COST} Stars`}
              </button>
            </div>
            {fightRefillError && (
              <p
                style={{
                  marginTop: 8,
                  fontSize: 13,
                  color: "#ff6b6b",
                }}
              >
                {fightRefillError}
              </p>
            )}
            <div className="modal-footer" style={{ marginTop: 10 }}>
              <button
                onClick={() => {
                  if (fightRefillLoading) return;
                  setFightRefillError("");
                  setShowFightRefillModal(false);
                  setRefillCard(null);
                }}
              >
                Maybe later
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* NEW: Confirm Evolve Modal */}
      {evolveConfirmOpen && evolveConfirmCard && (
        <div className="modal-backdrop">
          <motion.div
            className="modal fancy-success-modal"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 300, damping: 20 }}
          >
            <img
              src="/finger.png"
              alt="Confirm"
              style={{ height: "80px", marginBottom: "10px" }}
            />

            <h2 style={{ color: "#32cd32", marginBottom: "10px" }}>
              Confirm Evolve?
            </h2>

          <div
  style={{
    fontSize: 22,
    lineHeight: "26px",
    textAlign: "center",
    color: "#fff",
    width: "100%",
    maxWidth: "100%",
    margin: "4px 0 8px",
  }}
>
  <div
    style={{
      width: "100%",
      maxWidth: "100%",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    }}
    title={evolveConfirmCard.name}
  >
    Evolving Card{" "}
    <span style={{ color: "#09BDFF" }}>
      {evolveConfirmCard.name}
    </span>
  </div>

  {(() => {
    const p = getEvolveOptions(evolveConfirmCard);

    if (p.isLegacy) {
      return (
        <div style={{ marginTop: 4 }}>
          for {Number(p.options?.sparks || 0)} Sparks?
        </div>
      );
    }

    return (
      <div
        style={{
          marginTop: 15,
          fontSize: 25,
          lineHeight: "28px",
          color: "#F8BA1E",
        }}
      >
        Choose How You Want To Pay:
      </div>
    );
  })()}
</div>
            {(() => {
  const p = getEvolveOptions(evolveConfirmCard);
  const runes = Number(p.options?.runes || 0);
  const cardPieces = Number(p.options?.cardPieces || 0);
  const sparks = Number(p.options?.sparks || 0);

return (
  <div
    className="modal-footer"
    style={{
      display: "flex",
      flexDirection: "column",
      gap: 10,
      marginTop: 10,
      alignItems: "center",
      width: "100%",
    }}
  >
    {cardPieces > 0 && (
      <div
        style={{
          width: "100%",
          marginTop: 5,
          padding: "10px 10px",
          borderRadius: 14,
          background: "rgba(0,0,0,0.18)",
          border: "1px solid rgba(255,255,255,0.12)",
          boxSizing: "border-box",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
            marginBottom: 8,
          }}
        >
          <div
            style={{
              color: "#ffdf5f",
              fontSize: 18,
              lineHeight: "18px",
              textShadow: "1px 1px #000",
              whiteSpace: "nowrap",
            }}
          >
            Inventory Pieces
          </div>

          <div
            style={{
              color: "#fff",
              fontSize: 18,
              lineHeight: "18px",
              textShadow: "1px 1px #000",
              display: "flex",
              alignItems: "center",
              gap: 5,
              whiteSpace: "nowrap",
            }}
          >
            <span style={{ color: "#ffdf5f", fontSize: 15 }}>
              To Evolve
            </span>
            <img
              src="/pieces.png"
              alt=""
              style={{ width: 18, height: 18, objectFit: "contain" }}
            />
            {Number(evolveConfirmCard?.pieces || 0)} / {cardPieces}
          </div>
        </div>

        {inventoryLoading ? (
          <div
            style={{
              color: "rgba(255,255,255,0.72)",
              fontSize: 13,
              textAlign: "center",
              padding: "2px 0",
            }}
          >
            Loading card pieces...
          </div>
        ) : claimableCardPieceItems.length > 0 ? (
          <div
  style={{
    display: "flex",
    alignItems: "stretch",
    justifyContent:
      claimableCardPieceItems.length <= 3 ? "center" : "flex-start",
    gap: 6,
    overflowX: "auto",
    WebkitOverflowScrolling: "touch",
    paddingBottom: 2,
    width: "100%",
  }}
>
            {claimableCardPieceItems.map((inv) => {
              const amount = getCardPieceItemAmount(inv);
              const busy =
                Number(pieceClaimBusyId) === Number(inv.user_item_id);

              return (
                <div
                  key={inv.user_item_id}
                  style={{
                    flex: "0 0 auto",
                    width: 78,
                    minHeight: 74,
                    border: "1px solid rgba(255,223,95,0.3)",
                    borderRadius: 12,
                    background: "rgba(35,20,10,0.86)",
                    color: "#fff",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 6,
                    padding: "7px 6px",
                    textShadow: "1px 1px #000",
                    fontFamily: '"Mouse Memoirs", sans-serif',
                    boxSizing: "border-box",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 4,
                      fontSize: 20,
                      lineHeight: "20px",
                      color: "#ffdf5f",
                    }}
                  >
                    <img
                      src="/pieces.png"
                      alt=""
                      style={{ width: 18, height: 18, objectFit: "contain" }}
                    />
                    +{amount}
                  </div>

                  <button
                    type="button"
                    onClick={() =>
                      claimCardPiecesIntoCard(inv, evolveConfirmCard)
                    }
                    disabled={!!pieceClaimBusyId || !!evolvingId}
                    style={{
                      width: 56,
                      height: 24,
                      border: "1px solid rgba(255,255,255,0.28)",
                      borderRadius: 7,
                      background: "linear-gradient(#42d85f, #168f32)",
                      boxShadow: "0 2px 0 rgba(0,0,0,0.35)",
                      color: "#fff",
                      cursor:
                        pieceClaimBusyId || evolvingId
                          ? "not-allowed"
                          : "pointer",
                      opacity: pieceClaimBusyId || evolvingId ? 0.65 : 1,
                      fontSize: 15,
                      lineHeight: "21px",
                      textAlign: "center",
                      textShadow: "1px 1px rgba(0,0,0,0.7)",
                      fontFamily: '"Mouse Memoirs", sans-serif',
                      padding: 0,
                    }}
                  >
                    {busy ? "..." : "Claim"}
                  </button>
                </div>
              );
            })}
          </div>
        ) : (
          <div
            style={{
              color: "rgba(255,255,255,0.72)",
              fontSize: 13,
              textAlign: "center",
              padding: "2px 0",
            }}
          >
            No unclaimed card pieces available.
          </div>
        )}

        {pieceClaimError && (
          <div
            style={{
              marginTop: 6,
              color: "#ff6b6b",
              fontSize: 13,
              textAlign: "center",
              textShadow: "1px 1px #000",
            }}
          >
            {pieceClaimError}
          </div>
        )}
      </div>
    )}

    {!p.isLegacy ? (
  <div
    style={{
      display: "flex",
      gap: 3,
      width: "100%",
      justifyContent: "center",
    }}
  >
    {sparks > 0 && (
      <button
        onClick={() => confirmEvolve("sparks")}
        disabled={!!evolvingId}
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 6,
        }}
      >
        {evolvingId ? (
          "Evolving..."
        ) : (
          <>
            <span>Sparks</span>
            <span
              style={{
                paddingLeft: "18px",
                backgroundImage: "url('/sparks.png')",
                backgroundSize: "16px",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "left center",
              }}
            >
              {sparks}
            </span>
          </>
        )}
      </button>
    )}

    {runes > 0 && (
      <button
        onClick={() => confirmEvolve("runes")}
        disabled={!!evolvingId}
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 6,
        }}
      >
        {evolvingId ? (
          "Evolving..."
        ) : (
          <>
            <span>Runes</span>
            <span
              style={{
                paddingLeft: "18px",
                backgroundImage: "url('/runes.png')",
                backgroundSize: "16px",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "left center",
              }}
            >
              {runes}
            </span>
          </>
        )}
      </button>
    )}

    {cardPieces > 0 && (() => {
  const currentPieces = Number(evolveConfirmCard?.pieces || 0);
  const hasEnoughPieces = currentPieces >= cardPieces;

  return (
    <button
      onClick={() => confirmEvolve("pieces")}
      disabled={!!evolvingId || !hasEnoughPieces}
      title={
        hasEnoughPieces
          ? "Evolve using card pieces"
          : `Need ${cardPieces - currentPieces} more pieces`
      }
      style={{
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
        opacity: hasEnoughPieces ? 1 : 0.55,
        cursor: hasEnoughPieces ? "pointer" : "not-allowed",
      }}
    >
      {evolvingId ? (
        "Evolving..."
      ) : (
        <>
          <span>Pieces</span>
          <span
            style={{
              paddingLeft: "18px",
              backgroundImage: "url('/pieces.png')",
              backgroundSize: "16px",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "left center",
            }}
          >
            {cardPieces}
          </span>
        </>
      )}
    </button>
  );
})()}
  </div>
) : (
  <button onClick={() => confirmEvolve("sparks")} disabled={!!evolvingId}>
    {evolvingId ? "Evolving..." : "Confirm"}
  </button>
)}

    <button
  onClick={() => {
    if (pieceClaimBusyId) return;
    setPieceClaimError("");
    setEvolveConfirmOpen(false);
    setEvolveConfirmCard(null);
  }}
  disabled={!!evolvingId || !!pieceClaimBusyId}
  style={{ minWidth: 140 }}
>
  Cancel
</button>
  </div>
);})()}

          </motion.div>
        </div>
      )}

      {abilityRefillOpen && abilityCard && (
  <div className="modal-backdrop no-css-anim">
    <motion.div
      className="modal fancy-success-modal"
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ type: "spring", stiffness: 300, damping: 20 }}
    >
      <img
        src="/runes.png"
        alt="Runes"
        style={{ width: 56, height: 56, marginBottom: 10 }}
      />

      <h2 style={{ color: "#ffbb01", marginBottom: 10 }}>
        Reset Ability Limit?
      </h2>

      <p
        style={{
          textShadow: "1px 1px #000",
          fontSize: "20px",
          color: "#ddd",
          lineHeight: "26px",
        }}
      >
        You reached the limit for ability changes.
        <br />
        Reset this counter for{" "}
        <b>{Number(abilityChangeInfo.refillPriceRunes ?? 0)} Runes</b>?
      </p>

      <div className="modal-footer" style={{ marginTop: 20 }}>
        <button onClick={resetAbilityLimit} disabled={abilityRefillBusy}>
          {abilityRefillBusy
            ? "Resetting..."
            : `Reset (${Number(abilityChangeInfo.refillPriceRunes ?? 0)} Runes)`}
        </button>

        <button
          onClick={() => {
            setAbilityRefillOpen(false);
            setAbilityTarget(null);
            setAbilityCard(null);
          }}
          disabled={abilityRefillBusy}
        >
          Cancel
        </button>
      </div>
    </motion.div>
  </div>
)}

      {abilityChangeOpen && abilityTarget && (
  <div className="modal-backdrop no-css-anim">
    <motion.div
      className="modal fancy-success-modal"
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ type: "spring", stiffness: 300, damping: 20 }}
    >
      <img
        src={`/a_${String(abilityTarget).toLowerCase()}.png`}
        alt={abilityTarget}
        style={{ width: 56, height: 56, marginBottom: 10 }}
      />

      <h2 style={{ color: "#ffbb01", marginBottom: 10 }}>
        Change {abilityTarget}?
      </h2>

      <p
        style={{
          textShadow: "1px 1px #000",
          fontSize: "20px",
          color: "#ddd",
          lineHeight: "26px",
        }}
      >
        This will replace <b>{abilityTarget}</b> with a random ability this card does not already have.
      </p>

      <div
        style={{
          marginTop: 12,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 10,
        }}
      >
        <div
  style={{
    fontSize: 18,
    fontWeight: "bold",
    color: "#ddd",
    textShadow: "1px 1px #000",
  }}
>
  {abilityChangeInfo.limitMode === "overall" ? "Used" : "Used today"}:{" "}
  {abilityChangeInfo.loading ? "..." : abilityChangeInfo.used ?? "—"}/
  {abilityChangeInfo.limit ?? "—"}
</div>

        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 10,
            padding: "10px 18px",
            borderRadius: 14,
            background: "rgba(0,0,0,0.35)",
            border: "1px solid rgba(255,255,255,0.15)",
            boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
            minWidth: 220,
          }}
        >
          <span
            style={{
              fontSize: 20,
              fontWeight: "bold",
              color: "#ffcc62",
              textShadow: "1px 1px #000",
            }}
          >
            Current Price
          </span>

          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              fontSize: 28,
              fontWeight: "bold",
              color: "#fff",
              textShadow: "1px 1px #000",
            }}
          >
            {abilityChangeCurrency && (
              <img
                src={abilityChangeCurrency === "runes" ? "/runes.png" : "/sparks.png"}
                alt={abilityChangeCurrency}
                style={{
                  width: 26,
                  height: 26,
                  objectFit: "contain",
                }}
              />
            )}
            {abilityChangeInfo.loading ? "..." : abilityChangePrice ?? "—"}
          </span>
        </div>
      </div>

      <div className="modal-footer" style={{ marginTop: 20 }}>
        <button
          onClick={submitAbilityChange}
          disabled={
            abilityChangeBusy ||
            abilityChangeInfo.loading ||
            !abilityChangeInfo.enabled ||
            !abilityChangeCurrency ||
            abilityChangePrice == null ||
            abilityChangePrice <= 0
          }
        >
          {abilityChangeBusy ? "Changing..." : "Confirm"}
        </button>

        <button
          onClick={() => {
            setAbilityChangeOpen(false);
            setAbilityTarget(null);
            setAbilityCard(null);
          }}
          disabled={abilityChangeBusy}
        >
          Cancel
        </button>
      </div>
    </motion.div>
  </div>
)}

      {/* RENAME CARD MODAL */}
      {renameOpen && renamingCard && (
        <div className="modal-backdrop no-css-anim">
          <motion.div
            className="modal fancy-success-modal"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 300, damping: 20 }}
          >
            <img
              src="/finger.png"
              alt="Rename"
              style={{ height: 80, marginBottom: 10 }}
            />
            <h2 style={{ color: "#ffdf5f", marginBottom: 10 }}>
              Rename This Card?
            </h2>

            <p
              style={{
                textShadow: "1px 1px #000",
                fontSize: 18,
                color: "#ddd",
                marginBottom: 10,
              }}
            >
              New name will cost{" "}
              <span style={{ color: "#ffbb01", fontWeight: "bold" }}>
                {nameChangePrice} Sparks
              </span>
              .
              <br />
              Cards with offensive names will be deleted.
            </p>

            <input
              type="text"
              maxLength={32}
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              style={{
                width: "100%",
                maxWidth: 260,
                margin: "0 auto",
                display: "block",
                padding: "6px 10px",
                borderRadius: 6,
                border: "1px solid #555",
                background: "#111",
                color: "#fff",
                textAlign: "center",
              }}
              placeholder="Enter new name"
            />

            <div className="modal-footer" style={{ marginTop: 20 }}>
              <button onClick={submitRename} disabled={renameBusy}>
                {renameBusy
                  ? "Renaming..."
                  : `Confirm (${nameChangePrice} Sparks)`}
              </button>
              <button
                onClick={() => {
                  setRenameOpen(false);
                  setRenamingCard(null);
                  setRenameValue("");
                }}
              >
                Cancel
              </button>
            </div>
          </motion.div>
        </div>
      )}

                  <AnimatePresence>
  {defenseDeckRequiredOpen && (
    <div className="modal-backdrop no-css-anim">
      <motion.div
        className="modal fancy-success-modal"
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        transition={{ type: "spring", stiffness: 300, damping: 20 }}
        style={{
          textAlign: "center",
        }}
      >
        <img
          src="/shield.png"
          alt="Defense Deck"
          style={{
            height: 76,
            marginBottom: 10,
            filter: "drop-shadow(0 3px 6px rgba(0,0,0,0.45))",
          }}
          onError={(e) => {
            e.currentTarget.src = "/finger.png";
          }}
        />

        <h2
          style={{
            color: "#ffdf5f",
            marginBottom: 10,
            textShadow: "0 1px 2px #000",
          }}
        >
          Defense Deck Required
        </h2>

        <p
          style={{
            textShadow: "1px 1px #000",
            fontSize: 22,
            color: "#ddd",
            lineHeight: "28px",
            marginBottom: 10,
          }}
        >
          You need to set your Defense Deck before starting a battle.
        </p>

        <div
          className="modal-footer"
          style={{
            marginTop: 20,
            display: "flex",
            justifyContent: "center",
            gap: 3,
            flexWrap: "wrap",
          }}
        >
          <button
            onClick={() => {
              setDefenseDeckRequiredOpen(false);
            }}
          >
            Later
          </button>

          <button
            onClick={() => {
              setDefenseDeckRequiredOpen(false);
              onNavigate?.("defenseDeck");
            }}
          >
            Defense Deck
          </button>
        </div>
      </motion.div>
    </div>
  )}
</AnimatePresence>

                  {/* Convert Pieces Confirmation Modal */}
{pieceConvertOpen && pieceConvertCard && (() => {
  const pieces = Number(pieceConvertCard?.pieces || 0);
  const rate = getPieceConversionRate(pieceConvertCard);
  const expectedSparks = rate > 0 ? Math.floor(pieces * rate) : null;

  return (
    <div className="modal-backdrop">
      <motion.div
        className="modal fancy-success-modal"
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", stiffness: 300, damping: 20 }}
      >
        <img
          src="/pieces.png"
          alt="Pieces"
          style={{ height: "70px", marginBottom: "10px" }}
        />

        <h2 style={{ color: "#ffdf5f", marginBottom: "10px" }}>
          Convert Pieces?
        </h2>

        <div
          style={{
            color: "#fff",
            fontSize: 22,
            lineHeight: "28px",
            textAlign: "center",
            textShadow: "1px 1px #000",
            marginBottom: 14,
          }}
        >
          Convert{" "}
          <span style={{ color: "#ffdf5f" }}>
            {pieces}
          </span>{" "}
          card pieces from{" "}
          <span style={{ color: "#09BDFF" }}>
            {pieceConvertCard.name}
          </span>{" "}
          into Sparks?
        </div>

        {expectedSparks !== null && (
          <div
            style={{
              color: "#F8BA1E",
              fontSize: 24,
              marginBottom: 14,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
              textShadow: "1px 1px #000",
            }}
          >
            <img
              src="/sparks.png"
              alt="Sparks"
              style={{ width: 22, height: 22, objectFit: "contain" }}
            />
            +{expectedSparks}
          </div>
        )}

        <div
          className="modal-footer"
          style={{
            display: "flex",
            gap: 8,
            justifyContent: "center",
            width: "100%",
          }}
        >
          <button
            type="button"
            onClick={confirmPieceConvert}
            disabled={pieceConvertBusy}
            style={{ minWidth: 130 }}
          >
            {pieceConvertBusy ? "Converting..." : "Confirm"}
          </button>

          <button
            type="button"
            onClick={closePieceConvertConfirm}
            disabled={pieceConvertBusy}
            style={{ minWidth: 130 }}
          >
            Cancel
          </button>
        </div>
      </motion.div>
    </div>
  );
})()}

                  {/* Convert Pieces Success Modal */}
{pieceConvertSuccess && (
  <div className="modal-backdrop">
    <motion.div
      className="modal fancy-success-modal"
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ type: "spring", stiffness: 300, damping: 20 }}
    >
      <img
        src="/sparks.png"
        alt="Sparks"
        style={{ height: "72px", marginBottom: "10px" }}
      />

      <h2 style={{ color: "#ffdf5f", marginBottom: "10px" }}>
        Pieces Converted!
      </h2>

      <div
        style={{
          color: "#fff",
          fontSize: 22,
          lineHeight: "30px",
          textAlign: "center",
          textShadow: "1px 1px #000",
          marginBottom: 16,
        }}
      >
        Converted{" "}
        <span style={{ color: "#ffdf5f", fontWeight: 800 }}>
          {pieceConvertSuccess.piecesConverted}
        </span>{" "}
        card pieces into{" "}
        <span style={{ color: "#F8BA1E", fontWeight: 800 }}>
          {pieceConvertSuccess.sparksAwarded}
        </span>{" "}
        Sparks!
      </div>

      <div className="modal-footer">
        <button
          type="button"
          onClick={() => setPieceConvertSuccess(null)}
        >
          OK
        </button>
      </div>
    </motion.div>
  </div>
)}

      {/* EXISTING: ERROR MODAL */}
      {showErrorModal && (
        <div className="modal-backdrop no-css-anim">
          <motion.div
            className="modal fancy-success-modal"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 300, damping: 20 }}
          >
            <img
              src="/finger.png"
              alt="Error"
              style={{
                height: "80px",
                marginBottom: "10px",
                transform: "rotate(180deg)",
              }}
            />
            <h2 style={{ color: "#ff4d4d", marginBottom: "10px" }}>
              Transaction Failed
            </h2>
            <p
              style={{
                textTransform: "capitalize",
                textShadow: "1px 1px #000",
                fontSize: "24px",
                color: "#ddd",
              }}
            >
              {errorMessage}
            </p>
            <div className="modal-footer">
              <button
                className="buy-runes-btn"
                onClick={() => onOpenShop?.("coin_2")}
              >
                Buy Runes
              </button>
              <br />
              <button
                onClick={() => {
                  setShowErrorModal(false);
                  setErrorMessage("");
                }}
              >
                Close
              </button>
            </div>
          </motion.div>
        </div>
      )}

      <DiscountOfferModal
        user={user}
        guardedNavigate={(to) => onNavigate?.(to)}
        blocked={false}
        onPurchased={(u) => setUser((prev) => ({ ...prev, ...u }))}
      />
    </div>


  );
}

export default Fight;
